import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
    TableHead,
  TableRow,
  Paper,
  Snackbar,
  TablePagination,
  useTheme,
  alpha
} from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
  import {
  Science as ScienceIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,

  Notes as NotesIcon,
  Scale as ScaleIcon,
  Assessment as VarianceIcon,
  Error as ErrorIcon,
  LocationOn as LocationOnIcon,
  Delete as DeleteIcon,
  Edit as EditNoteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  Inventory as InventoryTwoIcon,
      Inventory2 as LotIcon,
    Close as CloseIcon,
    Add as AddIcon,
    Description as DescriptionIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import ChemicalTable from '../../inventory/inventory-components/shared/inventory-table-components/ChemicalTable';
import { addChemicalConfig } from '../../inventory/inventory-components/Add/addConfigs/addChemicalConfig';
import {
  convertUnits,
  formatUnitDisplay,
  areUnitsCompatible,
  getSuggestedUnits,
  getUnitType,
  WEIGHT_UNITS,
  VOLUME_UNITS
} from '../../../utils/unitConversions';

const BatchIngredients = ({
  batch,
  rawMaterials,
  formulations,
  showSnackbar,
  userCompanyId,
  onUpdate,
  user
}) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, canDeletePage } = usePermissions();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [scalingFactor, setScalingFactor] = useState(1);
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [conditionMenuAnchor, setConditionMenuAnchor] = useState(null);
  const [selectedIngredientForCondition, setSelectedIngredientForCondition] = useState(null);
  const [unitConversionWarning, setUnitConversionWarning] = useState('');
  const [amountEditAnchor, setAmountEditAnchor] = useState(null);
  const [selectedIngredientForAmount, setSelectedIngredientForAmount] = useState(null);
  const [quickEditAmount, setQuickEditAmount] = useState('');
  const [quickEditUnit, setQuickEditUnit] = useState('');

  const [notesEditAnchor, setNotesEditAnchor] = useState(null);
  const [selectedIngredientForNotes, setSelectedIngredientForNotes] = useState(null);
  const [quickEditNotes, setQuickEditNotes] = useState('');
  const [savingAmount, setSavingAmount] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryItemsData, setInventoryItemsData] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [expandedInventoryRows, setExpandedInventoryRows] = useState(new Set());
  const [productionLocationName, setProductionLocationName] = useState('');
  const [productionNotes, setProductionNotes] = useState('');
  const [savingProductionNotes, setSavingProductionNotes] = useState(false);
  const [showSavedNotesDialog, setShowSavedNotesDialog] = useState(false);
  const [batchNotes, setBatchNotes] = useState([]);
  const [loadingBatchNotes, setLoadingBatchNotes] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [savingEditNote, setSavingEditNote] = useState(false);
  const [lotBatchSelections, setLotBatchSelections] = useState({});
  const [showLotBatchDialog, setShowLotBatchDialog] = useState(false);
  const [selectedIngredientForLotBatch, setSelectedIngredientForLotBatch] = useState(null);
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [lotSearch, setLotSearch] = useState('');
  const [remainingQuantityNeeded, setRemainingQuantityNeeded] = useState(0);
  const [savingLotSelections, setSavingLotSelections] = useState(false);
  const [lotSummaries, setLotSummaries] = useState({});
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(3);

  // Accordion state management with localStorage persistence
  const [accordionStates, setAccordionStates] = useState({
    scalingInfo: false,
    progressOverview: false,
    inventoryOverview: false, // Default to collapsed
    phases: {} // Dynamic object for phase accordions
  });

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`batchIngredients_accordions_${batch?.id || 'default'}`);
    if (savedStates) {
      try {
        setAccordionStates(JSON.parse(savedStates));
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
    
    // Suppress ResizeObserver errors globally
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && args[0].toString().includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalConsoleError.apply(console, args);
    };
    
    // Also handle as window error event
    const handleResizeObserverError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
    };
    
    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      // Restore original console.error
      console.error = originalConsoleError;
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, [batch?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    localStorage.setItem(`batchIngredients_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Update phase accordion state
  const updatePhaseAccordionState = (phaseId, expanded) => {
    const newStates = { 
      ...accordionStates, 
      phases: { ...accordionStates.phases, [phaseId]: expanded }
    };
    setAccordionStates(newStates);
    localStorage.setItem(`batchIngredients_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Get all available units for dropdowns
  const getAllUnits = () => [...WEIGHT_UNITS, ...VOLUME_UNITS];

  useEffect(() => {
    if (batch?.formulation_id) {
      loadFormulationIngredients();
      loadInventoryData();
    }
    if (batch?.id) {
      loadBatchNotes();
      loadBatchIngredients();
    }
  }, [batch?.formulation_id, batch?.planned_quantity, batch?.production_location, batch?.id]);

  // Auto-generate ingredients after formulation ingredients are loaded
  useEffect(() => {
    if (batch?.id && formulationIngredients.length > 0) {
      checkAndGenerateIngredients();
    }
  }, [batch?.id, formulationIngredients.length]);

  const checkAndGenerateIngredients = async () => {
    try {
      const { data, error } = await supabase
        .from('batch_ingredients')
        .select('id')
        .eq('batch_id', batch.id);

      if (error) throw error;
      
      // If no ingredients exist, automatically generate them
      if (!data || data.length === 0) {
        await generateBatchIngredients();
      }
    } catch (error) {
      console.error('Error checking for existing ingredients:', error);
    }
  };

  useEffect(() => {
    if (ingredients.length > 0) {
      loadAllLotSummaries();
    }
  }, [ingredients]);

  useEffect(() => {
    if (batch?.planned_quantity && formulationIngredients.length > 0) {
      calculateScalingFactor();
    }
  }, [batch?.planned_quantity, formulationIngredients]);



  const loadFormulationIngredients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          *,
          raw_materials:ingredient_id (
            id, name, cas_number, sku, category, 
            material_type, primary_unit_measure, color, form,
            density_g_ml, primary_unit_type, primary_unit_quantity
          ),
          formulation_phases:formulation_phase_id (
            id, name, sequence
          )
        `)
        .eq('formulation_id', batch.formulation_id)
        .order('sequence');

      if (error) throw error;
      setFormulationIngredients(data || []);
    } catch (error) {
      console.error('Error loading formulation ingredients:', error);
      showSnackbar('Error loading formulation ingredients: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchIngredients = async (showFeedback = false) => {
    if (showFeedback) setLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('batch_ingredients')
        .select(`
          *,
          raw_materials:raw_material_id (
            id, name, cas_number, sku, category, 
            material_type, primary_unit_measure, color, form,
            density_g_ml, primary_unit_type, primary_unit_quantity
          ),
          formulation_phases:formulation_phase_id (
            id, name, sequence
          )
        `)
        .eq('batch_id', batch.id);

      if (error) throw error;
      
      // Sort ingredients by phase sequence, then by ingredient sequence (same order as formulation ingredients tab)
      const sortedIngredients = (data || []).sort((a, b) => {
        // First sort by phase sequence
        const phaseSeqA = a.formulation_phases?.sequence || 999;
        const phaseSeqB = b.formulation_phases?.sequence || 999;
        if (phaseSeqA !== phaseSeqB) {
          return phaseSeqA - phaseSeqB;
        }
        
        // Then sort by ingredient sequence within the phase
        const seqA = a.sequence_number || 999;
        const seqB = b.sequence_number || 999;
        return seqA - seqB;
      });
      
      setIngredients(sortedIngredients);
      
      if (showFeedback) {
        const count = sortedIngredients.length;
        showSnackbar(`Data refreshed! Loaded ${count} ingredient${count !== 1 ? 's' : ''}.`, 'success');
      }
      
    } catch (error) {
      console.error('Error loading batch ingredients:', error);
      if (showFeedback) {
        showSnackbar('Error loading ingredients: ' + error.message, 'error');
      }
    } finally {
      if (showFeedback) setLoading(false);
    }
  };

  const loadInventoryData = async () => {
    try {
      setLoadingInventory(true);
      
      // Get location name for display if batch has a production location ID
      if (batch?.production_location) {
        const isLocationId = batch.production_location.includes('-') && batch.production_location.length > 20;
        
        if (isLocationId) {
          const { data: locationData, error: locationError } = await supabase
            .from('locations')
            .select('name')
            .eq('id', batch.production_location)
            .eq('company_id', userCompanyId)
            .single();
          
          if (!locationError && locationData) {
            setProductionLocationName(locationData.name);
          } else {
            setProductionLocationName(batch.production_location);
          }
        } else {
          setProductionLocationName(batch.production_location);
        }
      }
      
      // Load aggregate inventory data
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('raw_materials_inventory')
        .select(`
          *,
          raw_material:raw_material_id(
            id,
            name,
            sku,
            primary_unit_type,
            primary_unit_quantity,
            primary_unit_measure,
            category,
            material_type,
            density_g_ml
          ),
          location:location_id(
            id,
            name
          )
        `)
        .eq('company_id', userCompanyId)
        .order('current_unit_quantity', { ascending: true });

      if (inventoryError) throw inventoryError;
      
      // Load detailed inventory items data (lot/batch level) with basic details
      const { data: inventoryItemsData, error: inventoryItemsError } = await supabase
        .from('raw_materials_inventory_items')
        .select(`
          *,
          raw_material:raw_material_id(
            id,
            name,
            sku,
            primary_unit_type,
            primary_unit_quantity,
            primary_unit_measure,
            category,
            material_type,
            density_g_ml
          ),
          location:location_id(
            id,
            name
          )
        `)
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('expiry_date', { ascending: true });

      if (inventoryItemsError) throw inventoryItemsError;

      // If we have inventory items, load their transaction details separately
      let transactionDetails = {};
      let supplierDetails = {};
      
      if (inventoryItemsData && inventoryItemsData.length > 0) {
        const transactionIds = inventoryItemsData
          .map(item => item.source_transaction_id)
          .filter(id => id);

        if (transactionIds.length > 0) {
          // Load transaction details
          const { data: transactionsData, error: transactionsError } = await supabase
            .from('raw_materials_transactions')
            .select('id, transaction_type, purchase_order_number, invoice_number, invoice_date, supplier_id, unit_cost, total_cost, notes')
            .in('id', transactionIds);

          if (!transactionsError && transactionsData) {
            transactionsData.forEach(transaction => {
              transactionDetails[transaction.id] = transaction;
            });

            // Load supplier details for transactions that have supplier_id
            const supplierIds = transactionsData
              .map(t => t.supplier_id)
              .filter(id => id);

            if (supplierIds.length > 0) {
              const { data: suppliersData, error: suppliersError } = await supabase
                .from('suppliers')
                .select('id, name')
                .in('id', supplierIds);

              if (!suppliersError && suppliersData) {
                suppliersData.forEach(supplier => {
                  supplierDetails[supplier.id] = supplier;
                });
              }
            }
          }
        }

        // Attach transaction and supplier details to inventory items
        inventoryItemsData.forEach(item => {
          if (item.source_transaction_id && transactionDetails[item.source_transaction_id]) {
            item.source_transaction = transactionDetails[item.source_transaction_id];
            if (item.source_transaction.supplier_id && supplierDetails[item.source_transaction.supplier_id]) {
              item.source_transaction.supplier = supplierDetails[item.source_transaction.supplier_id];
            }
          }
        });
      }
      
      setInventoryData(inventoryData || []);
      setInventoryItemsData(inventoryItemsData || []);
    } catch (error) {
      console.error('Error loading inventory data:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const loadBatchNotes = async () => {
    if (!batch?.id) return;
    
    try {
      setLoadingBatchNotes(true);
      
      const { data, error } = await supabase
        .from('batch_notes')
        .select(`
          *
        `)
        .eq('batch_id', batch.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBatchNotes(data || []);
    } catch (error) {
      console.error('Error loading batch notes:', error);
      showSnackbar('Error loading batch notes: ' + error.message, 'error');
    } finally {
      setLoadingBatchNotes(false);
    }
  };

  const deleteBatchNote = async (noteId) => {
    try {
      setDeletingNoteId(noteId);
      
      // If user has delete permission, allow deleting any note, otherwise only their own
      let deleteQuery = supabase
        .from('batch_notes')
        .delete()
        .eq('id', noteId);
      
      if (!canDeletePage('batch_ingredients')) {
        // Only allow deleting own notes if no delete permission
        deleteQuery = deleteQuery.eq('created_by', user.id);
      }
      
      const { data, error } = await deleteQuery;
      
      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      await loadBatchNotes();
      showSnackbar('Note deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting note:', error);
      showSnackbar('Error deleting note: ' + error.message, 'error');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const startEditNote = (note) => {
    setEditingNote(note);
    setEditNoteContent(note.note_content);
  };

  const cancelEditNote = () => {
    setEditingNote(null);
    setEditNoteContent('');
  };

  const saveEditNote = async () => {
    if (!editingNote || !editNoteContent.trim()) return;
    
    try {
      setSavingEditNote(true);
      
      // If user has edit permission, allow editing any note, otherwise only their own
      let updateQuery = supabase
        .from('batch_notes')
        .update({
          note_content: editNoteContent.trim()
        })
        .eq('id', editingNote.id);
      
      if (!canEditPage('batch_ingredients')) {
        // Only allow editing own notes if no edit permission
        updateQuery = updateQuery.eq('created_by', user.id);
      }
      
      const { error } = await updateQuery;
      
      if (error) throw error;
      
      await loadBatchNotes();
      showSnackbar('Note updated successfully!', 'success');
      cancelEditNote();
    } catch (error) {
      console.error('Error updating note:', error);
      showSnackbar('Error updating note: ' + error.message, 'error');
    } finally {
      setSavingEditNote(false);
    }
  };

  const deleteAllBatchNotes = async () => {
    try {
      setLoadingBatchNotes(true);
      
      // If user has delete permission, allow deleting all notes, otherwise only their own
      let deleteQuery = supabase
        .from('batch_notes')
        .delete()
        .eq('batch_id', batch.id);
      
      if (!canDeletePage('batch_ingredients')) {
        // Only allow deleting own notes if no delete permission
        deleteQuery = deleteQuery.eq('created_by', user.id);
      }
      
      const { error } = await deleteQuery;
      
      if (error) throw error;
      
      await loadBatchNotes();
      showSnackbar('All notes deleted successfully!', 'success');
      setShowSavedNotesDialog(false);
    } catch (error) {
      console.error('Error deleting all notes:', error);
      showSnackbar('Error deleting all notes: ' + error.message, 'error');
    } finally {
      setLoadingBatchNotes(false);
    }
  };



  const calculateScalingFactor = () => {
    const formulation = formulations.find(f => f.id === batch.formulation_id);
    if (formulation && formulation.target_batch_size && batch.planned_quantity) {
      // Use same density-based scaling logic as BatchOverview
      const formulationBatchSize = formulation.target_batch_size;
      const formulationUnit = formulation.unit_of_measure || formulation.target_batch_unit || 'kg';
      const targetBatchSize = batch.planned_quantity;
      const targetUnit = batch.planned_unit_of_measure || 'oz';

      // Calculate weighted average density from formulation ingredients (SAME AS BATCH OVERVIEW)
      let calculatedDensity = null;
      
      if (formulationIngredients.length > 0) {
        let totalWeightedDensity = 0;
        let totalPercentage = 0;
        
        formulationIngredients.forEach(ingredient => {
          const rawMaterial = ingredient.raw_materials;
          const percentage = ingredient.percentage || 0;
          const materialDensity = rawMaterial?.density_g_ml;
          
          if (materialDensity && materialDensity > 0 && percentage > 0) {
            totalWeightedDensity += (materialDensity * percentage);
            totalPercentage += percentage;
          }
        });
        
        if (totalPercentage > 0) {
          calculatedDensity = totalWeightedDensity / totalPercentage;
        }
      }

      // Calculate scaling factor - USE SAME LOGIC AS BATCH OVERVIEW
      let factor = 1;
      
      try {
        if (targetUnit !== formulationUnit) {
          // Check if we're trying to convert between volume and weight
          const targetUnitType = getUnitType(targetUnit);
          const formulationUnitType = getUnitType(formulationUnit);
          
          if (targetUnitType !== formulationUnitType) {
            // Different unit types (volume vs weight) - use calculated density
            const density = calculatedDensity || 1.0;
            
            if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
              // Convert formulation volume to weight using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              factor = targetBatchSize / formulationInTargetUnits;
            } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
              // Convert formulation weight to volume using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              factor = targetBatchSize / formulationInTargetUnits;
            }
          } else {
            // Same unit types - safe to convert
            const targetInFormulationUnits = convertUnits(targetBatchSize, targetUnit, formulationUnit);
            factor = targetInFormulationUnits / formulationBatchSize;
          }
        } else {
          factor = targetBatchSize / formulationBatchSize;
        }
      
      } catch (error) {
        console.warn('Unit conversion failed for scaling factor, using density-based fallback:', error);
        // Use density-based scaling with unitConversions.js
        const density = calculatedDensity || 1.0;
        const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
        factor = targetBatchSize / formulationInTargetUnits;
      }
      
      setScalingFactor(factor);
    }
  };

  const handleGenerateClick = () => {
    if (!batch.id || formulationIngredients.length === 0) return;

    // Check if there are existing ingredients with data
    const hasExistingData = ingredients.some(ingredient => 
      ingredient.actual_amount || 
      ingredient.supplier_lot_number || 
      ingredient.supplier_batch_number || 
      ingredient.usage_notes || 
      ingredient.material_condition
    );

    if (hasExistingData) {
      setShowGenerateConfirm(true);
    } else {
      generateBatchIngredients();
    }
  };

  const generateBatchIngredients = async () => {
    setLoading(true);
    try {
      // Delete existing batch ingredients
      await supabase
        .from('batch_ingredients')
        .delete()
        .eq('batch_id', batch.id);

      // Get formulation details for proper scaling (SAME LOGIC AS BATCH OVERVIEW)
      const formulation = formulations.find(f => f.id === batch.formulation_id);
      const formulationBatchSize = formulation?.target_batch_size || 100;
      const formulationUnit = formulation?.unit_of_measure || formulation?.target_batch_unit || 'kg';
      const targetBatchSize = batch.planned_quantity;
      const targetUnit = batch.planned_unit_of_measure || 'oz';

      // Calculate weighted average density from formulation ingredients (SAME AS BATCH OVERVIEW)
      let calculatedDensity = null;
      
      if (formulationIngredients.length > 0) {
        let totalWeightedDensity = 0;
        let totalPercentage = 0;
        
        formulationIngredients.forEach(ingredient => {
          const rawMaterial = ingredient.raw_materials;
          const percentage = ingredient.percentage || 0;
          const materialDensity = rawMaterial?.density_g_ml;
          
          if (materialDensity && materialDensity > 0 && percentage > 0) {
            totalWeightedDensity += (materialDensity * percentage);
            totalPercentage += percentage;
          }
        });
        
        if (totalPercentage > 0) {
          calculatedDensity = totalWeightedDensity / totalPercentage;
        }
      }

      // Calculate scaling factor - USE SAME LOGIC AS BATCH OVERVIEW
      let scalingFactor = 1;
      let targetInFormulationUnits = targetBatchSize;
      
      try {
        if (targetUnit !== formulationUnit) {
          // Check if we're trying to convert between volume and weight
          const targetUnitType = getUnitType(targetUnit);
          const formulationUnitType = getUnitType(formulationUnit);
          
          if (targetUnitType !== formulationUnitType) {
            // Different unit types (volume vs weight) - use calculated density
            const density = calculatedDensity || 1.0;
            
            if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
              // Convert formulation volume to weight using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              scalingFactor = targetBatchSize / formulationInTargetUnits;
            } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
              // Convert formulation weight to volume using unitConversions.js with density
              const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
              scalingFactor = targetBatchSize / formulationInTargetUnits;
            }
          } else {
            // Same unit types - safe to convert
            targetInFormulationUnits = convertUnits(targetBatchSize, targetUnit, formulationUnit);
            scalingFactor = targetInFormulationUnits / formulationBatchSize;
          }
        } else {
          scalingFactor = targetBatchSize / formulationBatchSize;
        }
      
      } catch (error) {
        console.warn('Unit conversion failed for batch ingredients, using density-based fallback:', error);
        // Use density-based scaling with unitConversions.js
        const density = calculatedDensity || 1.0;
        const formulationInTargetUnits = convertUnits(formulationBatchSize, formulationUnit, targetUnit, density);
        scalingFactor = targetBatchSize / formulationInTargetUnits;
      }

      // Create scaled ingredients from formulation
      const batchIngredientsData = formulationIngredients.map(formIngredient => {
        // Calculate ingredient amount using formulation base amounts with proper scaling
        const percentageDecimal = (formIngredient.percentage || 0) / 100;
        const baseIngredientAmount = formulationBatchSize * percentageDecimal;
        const scaledAmount = baseIngredientAmount * scalingFactor;
        
        // Use formulation unit as default, or raw material unit if available
        const defaultUnit = formulationUnit || formIngredient.raw_materials?.primary_unit_measure || 'kg';
        
        return {
          batch_id: batch.id,
          raw_material_id: formIngredient.ingredient_id,
          formulation_phase_id: formIngredient.formulation_phase_id,
          sequence_number: formIngredient.sequence,
          planned_percentage: formIngredient.percentage,
          planned_amount: scaledAmount > 0 ? scaledAmount : 0.001,
          planned_unit: defaultUnit,
          added_by: user?.id,
          added_at: new Date().toISOString()
        };
      });

      const { error } = await supabase
        .from('batch_ingredients')
        .insert(batchIngredientsData);

      if (error) throw error;

      await loadBatchIngredients();
      showSnackbar('Batch ingredients generated successfully!', 'success');
      onUpdate();
    } catch (error) {
      console.error('Error generating batch ingredients:', error);
      showSnackbar('Error generating batch ingredients: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient({
      ...ingredient,
      actual_amount: ingredient.actual_amount || '',
      actual_unit: ingredient.actual_unit || ingredient.planned_unit,
      material_condition: ingredient.material_condition || 'Good',
      usage_notes: ingredient.usage_notes || ''
    });
    setEditDialogOpen(true);
    setUnitConversionWarning('');
  };

  // Helper function to refresh edit dialog if it's open for the same ingredient
  const refreshEditDialogIfOpen = (ingredientId) => {
    if (editDialogOpen && editingIngredient?.id === ingredientId) {
      const updatedIngredient = ingredients.find(ing => ing.id === ingredientId);
      if (updatedIngredient) {
        setEditingIngredient({
          ...updatedIngredient,
          actual_amount: updatedIngredient.actual_amount || '',
          actual_unit: updatedIngredient.actual_unit || updatedIngredient.planned_unit,
          material_condition: updatedIngredient.material_condition || 'Good',
          usage_notes: updatedIngredient.usage_notes || ''
        });
      }
    }
  };

  const handleUnitChange = (newUnit) => {
    if (!editingIngredient) return;
    
    const oldUnit = editingIngredient.actual_unit;
    const amount = parseFloat(editingIngredient.actual_amount);
    
    if (oldUnit !== newUnit && amount > 0) {
      // Check if units are compatible
      const rawMaterial = editingIngredient.raw_materials;
      const density = rawMaterial?.density_g_ml;
      
      if (areUnitsCompatible(oldUnit, newUnit, !!density)) {
        try {
          // Convert the amount to new unit
          const convertedAmount = convertUnits(amount, oldUnit, newUnit, density);
          setEditingIngredient({
            ...editingIngredient,
            actual_amount: convertedAmount.toFixed(3),
            actual_unit: newUnit
          });
          setUnitConversionWarning(`Converted ${amount} ${oldUnit} to ${convertedAmount.toFixed(3)} ${newUnit}`);
        } catch (error) {
          setUnitConversionWarning(`Warning: Could not convert from ${oldUnit} to ${newUnit}. Please verify the amount.`);
          setEditingIngredient({
            ...editingIngredient,
            actual_unit: newUnit
          });
        }
      } else {
        setUnitConversionWarning(`Warning: ${oldUnit} and ${newUnit} are not compatible. Please verify the amount.`);
        setEditingIngredient({
          ...editingIngredient,
          actual_unit: newUnit
        });
      }
    } else {
      setEditingIngredient({
        ...editingIngredient,
        actual_unit: newUnit
      });
      setUnitConversionWarning('');
    }
  };

  const calculateVariance = (planned, plannedUnit, actual, actualUnit, density) => {
    if (!planned || !actual) return null;
    
    try {
      // Convert actual amount to planned unit for comparison
      let actualInPlannedUnits = actual;
      if (plannedUnit !== actualUnit) {
        actualInPlannedUnits = convertUnits(actual, actualUnit, plannedUnit, density);
      }
      
      const variance = actualInPlannedUnits - planned;
      const variancePercentage = (variance / planned) * 100;
      
      return {
        variance,
        variancePercentage,
        actualInPlannedUnits
      };
    } catch (error) {
      console.error('Error calculating variance:', error);
      return null;
    }
  };

  const handleSaveIngredient = async () => {
    setLoading(true);
    try {
      const actualAmount = parseFloat(editingIngredient.actual_amount) || null;
      let varianceAmount = null;
      let variancePercentage = null;

      // Calculate variance if actual amount is provided
      if (actualAmount && editingIngredient.planned_amount) {
        const density = editingIngredient.raw_materials?.density_g_ml;
        const varianceCalc = calculateVariance(
          editingIngredient.planned_amount,
          editingIngredient.planned_unit,
          actualAmount,
          editingIngredient.actual_unit,
          density
        );
        
        if (varianceCalc) {
          varianceAmount = varianceCalc.variance;
          variancePercentage = varianceCalc.variancePercentage;
        }
      }

      const updateData = {
        actual_amount: actualAmount,
        actual_unit: editingIngredient.actual_unit,
        variance_amount: varianceAmount,
        variance_percentage: variancePercentage,
        material_condition: editingIngredient.material_condition,
        usage_notes: editingIngredient.usage_notes
      };

      const { error } = await supabase
        .from('batch_ingredients')
        .update(updateData)
        .eq('id', editingIngredient.id);

      if (error) throw error;

      setEditDialogOpen(false);
      setEditingIngredient(null);
      setUnitConversionWarning('');
      await loadBatchIngredients();
      showSnackbar('Ingredient updated successfully!', 'success');
      checkAndUpdateCompletion();
    } catch (error) {
      console.error('Error updating ingredient:', error);
      showSnackbar('Error updating ingredient: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getVarianceIcon = (variance) => {
    if (!variance) return null;
    if (variance > 0) return <TrendingUpIcon fontSize="small" color="warning" />;
    if (variance < 0) return <TrendingDownIcon fontSize="small" color="info" />;
    return <RemoveIcon fontSize="small" color="success" />;
  };

  const getVarianceColor = (variance) => {
    if (!variance) return 'default';
    if (Math.abs(variance) > 10) return 'error';
    if (Math.abs(variance) > 5) return 'warning';
    return 'success';
  };

  const getConditionColor = (condition) => {
    const colors = {
      'Good': 'success',
      'Acceptable': 'warning', 
      'Damaged': 'error',
      'Expired': 'error'
    };
    return colors[condition] || 'default';
  };

  const formatAmount = (amount, unit) => {
    if (!amount) return 'Not set';
    return formatUnitDisplay(amount, unit, 3);
  };

  // Calculate raw material conversion amounts (same logic as IngredientsTab)
  const calculateRawMaterialAmountNeeded = (amount, formulationUnit, rawMaterial) => {
    if (!rawMaterial || !amount) return null;
    
    const materialDensity = rawMaterial.density_g_ml || 1.0;
    const rawMaterialUnit = rawMaterial.primary_unit_measure;
    
    // If same units, no conversion needed
    if (!rawMaterialUnit || rawMaterialUnit === formulationUnit) {
      return null;
    }

    try {
      // Convert formulation amount to raw material units using unitConversions.js with density
      const rawMaterialAmountNeeded = convertUnits(
        amount,
        formulationUnit,
        rawMaterialUnit,
        materialDensity
      );

      return {
        amount: rawMaterialAmountNeeded.toFixed(3),
        unit: rawMaterialUnit,
        density: materialDensity.toFixed(3)
      };
    } catch (error) {
      console.warn(`Raw material unit conversion failed for ${rawMaterial?.name}: ${error.message}`);
      return null;
    }
  };

  const formatPercentage = (percentage) => {
    if (!percentage) return 'N/A';
    return `${parseFloat(percentage).toFixed(4)}%`;
  };

  const handleConditionClick = (event, ingredient) => {
    setConditionMenuAnchor(event.currentTarget);
    setSelectedIngredientForCondition(ingredient);
  };

  const handleConditionClose = () => {
    setConditionMenuAnchor(null);
    setSelectedIngredientForCondition(null);
  };

  const handleAmountClick = (event, ingredient) => {
    setAmountEditAnchor(event.currentTarget);
    setSelectedIngredientForAmount(ingredient);
    setQuickEditAmount(ingredient.actual_amount || '');
    setQuickEditUnit(ingredient.actual_unit || ingredient.planned_unit);
  };

  const handleUsePlannedAmount = () => {
    if (!selectedIngredientForAmount) return;
    setQuickEditAmount(selectedIngredientForAmount.planned_amount?.toString() || '');
    setQuickEditUnit(selectedIngredientForAmount.planned_unit);
  };

  const handleAmountEditClose = () => {
    setAmountEditAnchor(null);
    setSelectedIngredientForAmount(null);
    setQuickEditAmount('');
    setQuickEditUnit('');
  };



  const handleNotesClick = (event, ingredient) => {
    setNotesEditAnchor(event.currentTarget);
    setSelectedIngredientForNotes(ingredient);
    setQuickEditNotes(ingredient.usage_notes || '');
  };

  const handleNotesEditClose = () => {
    setNotesEditAnchor(null);
    setSelectedIngredientForNotes(null);
    setQuickEditNotes('');
  };

    const handleConditionChange = async (newCondition) => {
    if (!selectedIngredientForCondition) return;

    try {
      const { error } = await supabase
        .from('batch_ingredients')
        .update({ material_condition: newCondition })
        .eq('id', selectedIngredientForCondition.id);

      if (error) throw error;
      
      await loadBatchIngredients();
      refreshEditDialogIfOpen(selectedIngredientForCondition.id);
      showSnackbar(`Condition updated to ${newCondition}`, 'success');
      checkAndUpdateCompletion();
    } catch (error) {
      console.error('Error updating condition:', error);
      showSnackbar('Error updating condition: ' + error.message, 'error');
    } finally {
      handleConditionClose();
    }
  };

  const handleQuickAmountSave = async () => {
    if (!selectedIngredientForAmount || savingAmount) return;

    setSavingAmount(true);
    try {
      const actualAmount = parseFloat(quickEditAmount) || null;
      let varianceAmount = null;
      let variancePercentage = null;
      let varianceMessage = '';

      // Calculate variance if actual amount is provided
      if (actualAmount && selectedIngredientForAmount.planned_amount) {
        const density = selectedIngredientForAmount.raw_materials?.density_g_ml;
        const varianceCalc = calculateVariance(
          selectedIngredientForAmount.planned_amount,
          selectedIngredientForAmount.planned_unit,
          actualAmount,
          quickEditUnit,
          density
        );
        
        if (varianceCalc) {
          varianceAmount = varianceCalc.variance;
          variancePercentage = varianceCalc.variancePercentage;
          
          // Determine variance message
          if (Math.abs(variancePercentage) < 0.01) {
            varianceMessage = 'Exact match - no variance detected!';
          } else if (variancePercentage > 0) {
            varianceMessage = `Amount updated with +${variancePercentage.toFixed(1)}% variance (over planned)`;
          } else {
            varianceMessage = `Amount updated with ${variancePercentage.toFixed(1)}% variance (under planned)`;
          }
        }
      } else {
        varianceMessage = 'Amount updated successfully!';
      }

      const updateData = {
        actual_amount: actualAmount,
        actual_unit: quickEditUnit,
        variance_amount: varianceAmount,
        variance_percentage: variancePercentage
      };

      const { error } = await supabase
        .from('batch_ingredients')
        .update(updateData)
        .eq('id', selectedIngredientForAmount.id);

      if (error) throw error;

      await loadBatchIngredients();
      refreshEditDialogIfOpen(selectedIngredientForAmount.id);
      showSnackbar(varianceMessage, Math.abs(variancePercentage || 0) < 0.01 ? 'success' : 'info');
      checkAndUpdateCompletion();
      
    } catch (error) {
      console.error('Error updating amount:', error);
      showSnackbar('Error updating amount: ' + error.message, 'error');
    } finally {
      setSavingAmount(false);
      handleAmountEditClose();
    }
  };



  const handleQuickNotesSave = async () => {
    if (!selectedIngredientForNotes || savingNotes) return;

    setSavingNotes(true);
    try {
      const updateData = {
        usage_notes: quickEditNotes || null
      };

      const { error } = await supabase
        .from('batch_ingredients')
        .update(updateData)
        .eq('id', selectedIngredientForNotes.id);

      if (error) throw error;

      await loadBatchIngredients();
      refreshEditDialogIfOpen(selectedIngredientForNotes.id);
      showSnackbar('Usage notes updated successfully!', 'success');
      checkAndUpdateCompletion();
      
    } catch (error) {
      console.error('Error updating notes:', error);
      showSnackbar('Error updating notes: ' + error.message, 'error');
    } finally {
      setSavingNotes(false);
      handleNotesEditClose();
    }
  };

  const handleSaveProgress = async () => {
    setLoading(true);
    try {
      // Refresh data to ensure everything is synced
      await loadBatchIngredients(false);
      
      // Call onUpdate to refresh parent components
      if (onUpdate) {
        onUpdate();
      }
      
      // Calculate completion stats for message
      const completedIngredients = ingredients.filter(i => i.actual_amount).length;
      const totalIngredients = ingredients.length;
      const completionRate = totalIngredients > 0 ? Math.round((completedIngredients / totalIngredients) * 100) : 0;
      
      showSnackbar(
        `Progress saved! ${completedIngredients}/${totalIngredients} ingredients completed (${completionRate}%)`,
        'success'
      );
      
    } catch (error) {
      console.error('Error saving progress:', error);
      showSnackbar('Error saving progress: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Check if all ingredients are fully completed and trigger onUpdate
  const checkAndUpdateCompletion = () => {
    if (ingredients.length > 0) {
      const allCompleted = ingredients.every(ingredient => 
        ingredient.actual_amount && 
        (ingredient.supplier_lot_number || ingredient.supplier_batch_number) &&
        ingredient.material_condition &&
        ingredient.planned_unit &&
        ingredient.actual_unit
      );
      
      if (allCompleted && onUpdate) {
        onUpdate();
      }
    }
  };

  const getUnitDisplayForIngredient = (ingredient) => {
    const rawMaterial = ingredient.raw_materials;
    if (rawMaterial?.primary_unit_measure) {
      return `${ingredient.planned_unit} (Material: ${rawMaterial.primary_unit_measure})`;
    }
    return ingredient.planned_unit;
  };

  const getInventoryForIngredient = (rawMaterialId) => {
    const allInventory = inventoryData.filter(inv => inv.raw_material_id === rawMaterialId);
    
    // Further filter by production location if specified
    if (batch?.production_location) {
      // Check if production_location is an ID (UUID format) or name
      const isLocationId = batch.production_location.includes('-') && batch.production_location.length > 20;
      
      return allInventory.filter(inv => {
        if (isLocationId) {
          return inv.location?.id === batch.production_location;
        } else {
          return inv.location?.name === batch.production_location;
        }
      });
    }
    
    return allInventory;
  };

  const getInventoryItemsForIngredient = (rawMaterialId, locationId = null) => {
    let filteredItems = inventoryItemsData.filter(item => item.raw_material_id === rawMaterialId);
    
    // Further filter by production location if specified
    if (batch?.production_location || locationId) {
      const targetLocation = locationId || batch.production_location;
      const isLocationId = targetLocation.includes('-') && targetLocation.length > 20;
      
      filteredItems = filteredItems.filter(item => {
        if (isLocationId) {
          return item.location?.id === targetLocation;
        } else {
          return item.location?.name === targetLocation;
        }
      });
    }
    
    // Sort by expiry date (closest expiry first), then by created date
    return filteredItems.sort((a, b) => {
      if (a.expiry_date && b.expiry_date) {
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      }
      if (a.expiry_date && !b.expiry_date) return -1;
      if (!a.expiry_date && b.expiry_date) return 1;
      return new Date(a.created_at) - new Date(b.created_at);
    });
  };

  const getTotalStockForIngredient = (rawMaterialId) => {
    const inventories = getInventoryForIngredient(rawMaterialId);
    return inventories.reduce((total, inv) => total + (inv.current_unit_quantity || 0), 0);
  };

  const getStockStatus = (rawMaterialId, plannedAmount = 0) => {
    const inventories = getInventoryForIngredient(rawMaterialId);
    if (inventories.length === 0) return { status: 'no-inventory', color: 'error', message: 'No inventory tracked' };
    
    const totalStock = getTotalStockForIngredient(rawMaterialId);
    const hasLowStock = inventories.some(inv => 
      inv.reorder_point_units > 0 && inv.current_unit_quantity <= inv.reorder_point_units
    );
    
    // Check for expiring items (within 30 days)
    const inventoryItems = getInventoryItemsForIngredient(rawMaterialId);
    const hasExpiringItems = inventoryItems.some(item => {
      if (!item.expiry_date) return false;
      const daysToExpiry = (new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
      return daysToExpiry <= 30 && daysToExpiry >= 0;
    });
    
    const hasExpiredItems = inventoryItems.some(item => {
      if (!item.expiry_date) return false;
      return new Date(item.expiry_date) < new Date();
    });
    
    if (totalStock === 0) return { status: 'out-of-stock', color: 'error', message: 'Out of stock' };
    if (hasExpiredItems) return { status: 'expired', color: 'error', message: 'Contains expired items' };
    if (totalStock < plannedAmount) return { status: 'insufficient', color: 'error', message: `Insufficient stock (${totalStock} available)` };
    if (hasExpiringItems) return { status: 'expiring', color: 'warning', message: 'Items expiring soon' };
    if (hasLowStock) return { status: 'low-stock', color: 'warning', message: 'Low stock warning' };
    return { status: 'in-stock', color: 'success', message: `${totalStock} units available` };
  };

  const toggleInventoryRowExpansion = (rawMaterialId, locationId) => {
    const key = `${rawMaterialId}-${locationId}`;
    const newExpanded = new Set(expandedInventoryRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedInventoryRows(newExpanded);
  };

  const isInventoryRowExpanded = (rawMaterialId, locationId) => {
    return expandedInventoryRows.has(`${rawMaterialId}-${locationId}`);
  };

  const formatExpirationStatus = (expiryDate) => {
    if (!expiryDate) return { text: 'No expiry', color: 'default', icon: null };
    
    const now = new Date();
    const expiry = new Date(expiryDate);
    const daysToExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysToExpiry < 0) {
      return { 
        text: `Expired ${Math.abs(daysToExpiry)} days ago`, 
        color: 'error', 
        icon: <ErrorIcon fontSize="small" />
      };
    } else if (daysToExpiry === 0) {
      return { 
        text: 'Expires today', 
        color: 'error', 
        icon: <WarningIcon fontSize="small" />
      };
    } else if (daysToExpiry <= 7) {
      return { 
        text: `Expires in ${daysToExpiry} day${daysToExpiry === 1 ? '' : 's'}`, 
        color: 'error', 
        icon: <WarningIcon fontSize="small" />
      };
    } else if (daysToExpiry <= 30) {
      return { 
        text: `Expires in ${daysToExpiry} days`, 
        color: 'warning', 
        icon: <WarningIcon fontSize="small" />
      };
    } else {
      return { 
        text: `Expires in ${daysToExpiry} days`, 
        color: 'success', 
        icon: <CheckCircleIcon fontSize="small" />
      };
    }
  };

  const loadAvailableLotsForIngredient = (ingredient) => {
    const inventoryItems = getInventoryItemsForIngredient(ingredient.raw_material_id);
    return inventoryItems.filter(item => item.current_unit_quantity > 0);
  };

  // Calculate how much of an inventory item is already allocated to other batch ingredients
  const calculateAllocationForInventoryItem = async (inventoryItemId, excludeIngredientId = null) => {
    try {
      let query = supabase
        .from('batch_ingredient_lots')
        .select('quantity_used, batch_ingredients!inner(batch_id)')
        .eq('inventory_item_id', inventoryItemId)
        .eq('batch_ingredients.batch_id', batch.id);

      if (excludeIngredientId) {
        query = query.neq('batch_ingredient_id', excludeIngredientId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).reduce((sum, allocation) => sum + (allocation.quantity_used || 0), 0);
    } catch (error) {
      console.error('Error calculating allocations:', error);
      return 0;
    }
  };

  // Calculate available quantity for a lot considering existing allocations in this batch
  const calculateAvailableQuantityWithAllocations = async (lot, excludeIngredientId = null) => {
    const allocatedQuantity = await calculateAllocationForInventoryItem(lot.id, excludeIngredientId);
    const originalQuantity = lot.current_total_quantity || 0;
    return Math.max(0, originalQuantity - allocatedQuantity);
  };

  const loadExistingLotSelections = async (ingredientId, rawMaterialId = null) => {
    try {
      const { data, error } = await supabase
        .from('batch_ingredient_lots')
        .select('*')
        .eq('batch_ingredient_id', ingredientId);

      if (error) throw error;
      


      // Get the ingredient to find raw material ID if not provided
      const ingredient = ingredients.find(ing => ing.id === ingredientId);
      const materialId = rawMaterialId || ingredient?.raw_material_id || selectedIngredientForLotBatch?.raw_material_id;

      if (!materialId) {
        // Get ingredient to find raw material unit info
        const ingredient = ingredients.find(ing => ing.id === ingredientId);
        const rawMaterial = ingredient?.raw_materials;
        
        // Return basic lot data without inventory enhancements if no material ID
        return (data || []).map(lot => ({
          inventory_item_id: lot.inventory_item_id,
          lot_number: lot.lot_number,
          batch_number: lot.batch_number,
          quantity_used: lot.quantity_used,
          unit: lot.unit_used,
          unit_type: rawMaterial?.primary_unit_type || 'units',
          unit_measure: rawMaterial?.primary_unit_measure || lot.unit_used || 'gal',
          available_total_quantity: lot.quantity_used || 0,
          expiry_date: lot.expiry_date,
          location_name: lot.location_name,
          manufactured_date: lot.manufactured_date
        }));
      }

      // Enhance existing selections with current inventory data and allocation info
      const enhancedSelections = [];
      for (const lot of (data || [])) {
        // Find the current inventory item to get available quantity
        const inventoryItems = getInventoryItemsForIngredient(materialId);
        const currentInventoryItem = inventoryItems.find(item => item.id === lot.inventory_item_id);
        
        if (currentInventoryItem) {
          const availableQty = await calculateAvailableQuantityWithAllocations(currentInventoryItem, ingredientId);
          
          enhancedSelections.push({
            inventory_item_id: lot.inventory_item_id,
            lot_number: lot.lot_number,
            batch_number: lot.batch_number,
            quantity_used: lot.quantity_used,
            unit: lot.unit_used,
            unit_type: currentInventoryItem.raw_material?.primary_unit_type || 'units',
            unit_measure: currentInventoryItem.raw_material?.primary_unit_measure || 'gal',
            available_total_quantity: availableQty + (lot.quantity_used || 0), // Add back the currently used amount to show what's available for this selection
            expiry_date: lot.expiry_date,
            location_name: lot.location_name,
            manufactured_date: lot.manufactured_date
          });
        } else {
          // Fallback if inventory item not found (might be deleted)
          // Get raw material info for proper units
          const ingredient = ingredients.find(ing => ing.id === ingredientId);
          const rawMaterial = ingredient?.raw_materials;
          
          enhancedSelections.push({
            inventory_item_id: lot.inventory_item_id,
            lot_number: lot.lot_number,
            batch_number: lot.batch_number,
            quantity_used: lot.quantity_used,
            unit: lot.unit_used,
            unit_type: rawMaterial?.primary_unit_type || 'units',
            unit_measure: rawMaterial?.primary_unit_measure || lot.unit_used || 'gal',
            available_total_quantity: lot.quantity_used || 0,
            expiry_date: lot.expiry_date,
            location_name: lot.location_name,
            manufactured_date: lot.manufactured_date
          });
        }
      }

      return enhancedSelections;
    } catch (error) {
      console.error('Error loading existing lot selections:', error);
      return [];
    }
  };

  const handleLotBatchAmountClick = async (ingredient) => {
    const rawLotsForIngredient = loadAvailableLotsForIngredient(ingredient);
    
    if (rawLotsForIngredient.length === 0) {
      showSnackbar('No available lots/batches found for this ingredient', 'warning');
      return;
    }

    // Use current total quantity as available (should be correct gal amount like 5000, not 5023)
    const lotsWithAvailability = [];
    for (const lot of rawLotsForIngredient) {
      const availableQty = lot.current_total_quantity || 0;
      if (availableQty > 0) {
        lotsWithAvailability.push({
          ...lot,
          available_quantity_after_allocations: availableQty,
          original_total_quantity: lot.current_total_quantity
        });
      }
    }

    if (lotsWithAvailability.length === 0) {
      showSnackbar('No lots/batches have available quantity after considering current allocations', 'warning');
      return;
    }

    setSelectedIngredientForLotBatch(ingredient);
    setAvailableLots(lotsWithAvailability);
    
    // Load existing lot selections from database
    const existingSelections = await loadExistingLotSelections(ingredient.id, ingredient.raw_material_id);
    setSelectedLots(existingSelections);
    
    // Calculate remaining quantity needed in RAW MATERIAL UNITS
    const plannedAmount = ingredient.planned_amount || 0;
    const plannedUnit = ingredient.planned_unit;
    const rawMaterial = ingredient.raw_materials;
    
    // Convert planned amount to raw material units if different
    let plannedAmountInRawUnits = plannedAmount;
    let displayUnit = plannedUnit;
    
    if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== plannedUnit) {
      try {
        plannedAmountInRawUnits = convertUnits(
          plannedAmount,
          plannedUnit,
          rawMaterial.primary_unit_measure,
          rawMaterial.density_g_ml || 1.0
        );
        displayUnit = rawMaterial.primary_unit_measure;
      } catch (error) {
        console.warn('Failed to convert planned amount to raw material units:', error);
      }
    }
    
    const currentTotalSelected = existingSelections.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
    setRemainingQuantityNeeded(Math.max(0, plannedAmountInRawUnits - currentTotalSelected));
    
    setShowLotBatchDialog(true);
  };

  const selectLotForUse = (lot) => {
    // Check if already selected
    const existingIndex = selectedLots.findIndex(sel => sel.inventory_item_id === lot.id);
    if (existingIndex >= 0) {
      showSnackbar('This lot/batch is already selected', 'warning');
      return;
    }

    // Get the actual available quantities - use current_total_quantity as the base (this should be correct gal amount)
    const availableUnits = lot.current_unit_quantity || 0; // e.g., 50 drums
    const availableTotalQuantity = lot.current_total_quantity || 0; // Use current total quantity in gal/liters (should be 5000, not 5023)
    const originalTotalQuantity = lot.original_total_quantity || lot.current_total_quantity || 0; // Keep original for display
    
    // Get unit info - prefer from lot's raw material, fall back to ingredient's raw material
    const unitType = lot.raw_material?.primary_unit_type || selectedIngredientForLotBatch?.raw_materials?.primary_unit_type || 'units';
    const unitMeasure = lot.raw_material?.primary_unit_measure || selectedIngredientForLotBatch?.raw_materials?.primary_unit_measure || selectedIngredientForLotBatch?.planned_unit || 'gal';

    // Calculate suggested quantity based on remaining needed (in total measure units)
    const suggestedQuantity = Math.min(remainingQuantityNeeded, availableTotalQuantity);

    const newSelection = {
      inventory_item_id: lot.id,
      lot_number: lot.lot_number,
      batch_number: lot.batch_number,
      available_units: availableUnits, // 50 drums
      available_total_quantity: availableTotalQuantity, // Raw inventory quantity (e.g., 5000 gal)
      quantity_used: suggestedQuantity, // Amount in gal
      unit: selectedIngredientForLotBatch.planned_unit, // Target unit for recipe
      unit_type: unitType, // drums
      unit_measure: unitMeasure, // gal
      expiry_date: lot.expiry_date,
      location_name: lot.location?.name,
      manufactured_date: lot.manufactured_date
    };

    setSelectedLots([...selectedLots, newSelection]);
    
    // Update remaining quantity
    const newRemaining = Math.max(0, remainingQuantityNeeded - suggestedQuantity);
    setRemainingQuantityNeeded(newRemaining);
  };

  const updateLotQuantity = (index, newQuantity) => {
    const updatedLots = [...selectedLots];
    const oldQuantity = updatedLots[index].quantity_used || 0;
    updatedLots[index].quantity_used = parseFloat(newQuantity) || 0;
    
    setSelectedLots(updatedLots);
    
    // Recalculate remaining quantity needed in RAW MATERIAL UNITS
    const plannedAmount = selectedIngredientForLotBatch?.planned_amount || 0;
    const plannedUnit = selectedIngredientForLotBatch?.planned_unit;
    const rawMaterial = selectedIngredientForLotBatch?.raw_materials;
    
    // Convert planned amount to raw material units if different
    let plannedAmountInRawUnits = plannedAmount;
    
    if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== plannedUnit) {
      try {
        plannedAmountInRawUnits = convertUnits(
          plannedAmount,
          plannedUnit,
          rawMaterial.primary_unit_measure,
          rawMaterial.density_g_ml || 1.0
        );
      } catch (error) {
        console.warn('Failed to convert planned amount to raw material units:', error);
      }
    }
    
    const currentTotalSelected = updatedLots.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
    setRemainingQuantityNeeded(Math.max(0, plannedAmountInRawUnits - currentTotalSelected));
  };

  const removeLotFromSelection = (index) => {
    const updatedLots = [...selectedLots];
    const removedQuantity = updatedLots[index].quantity_used || 0;
    updatedLots.splice(index, 1);
    
    setSelectedLots(updatedLots);
    
    // Update remaining quantity
    setRemainingQuantityNeeded(remainingQuantityNeeded + removedQuantity);
  };

  const validateLotSelections = () => {
    if (selectedLots.length === 0) {
      showSnackbar('Please select at least one lot/batch', 'error');
      return false;
    }

    // Check if any lot exceeds available quantity
    for (const selection of selectedLots) {
      const availableLot = availableLots.find(lot => lot.id === selection.inventory_item_id);
      if (!availableLot) {
        showSnackbar('Selected lot is no longer available', 'error');
        return false;
      }
      
      // Compare quantity_used to raw inventory quantity - no weird calculations
      const availableQuantity = availableLot.current_total_quantity || 0; // Raw inventory amount (e.g., 5000 gal)
      const requestedQuantity = selection.quantity_used || 0;
      
      if (requestedQuantity > availableQuantity) {
        const lotName = selection.lot_number || selection.batch_number || 'Unknown';
        const unitName = selection.unit_measure || selectedIngredientForLotBatch?.raw_materials?.primary_unit_measure || selectedIngredientForLotBatch?.planned_unit || 'gal';
        showSnackbar(
          `Quantity for lot ${lotName} (${requestedQuantity}) exceeds available stock (${availableQuantity} ${unitName} available)`, 
          'error'
        );
        return false;
      }
      
      if (requestedQuantity <= 0) {
        const lotName = selection.lot_number || selection.batch_number || 'Unknown';
        showSnackbar(`Please specify a quantity for lot ${lotName}`, 'error');
        return false;
      }
    }

    return true;
  };

  const saveLotBatchSelections = async () => {
    if (!validateLotSelections()) return;
    
    if (!selectedIngredientForLotBatch?.id) {
      showSnackbar('No ingredient selected for lot/batch assignment', 'error');
      return;
    }
    
    if (!user?.id) {
      showSnackbar('User authentication required', 'error');
      return;
    }
    
    setSavingLotSelections(true);
    try {
      const ingredient = selectedIngredientForLotBatch;
      const totalQuantityUsedInRawUnits = selectedLots.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
      
      // Store actual amount in RAW MATERIAL UNITS (the actual units used)
      const rawMaterial = ingredient.raw_materials;
      let actualAmount = totalQuantityUsedInRawUnits;
      let actualUnit = rawMaterial?.primary_unit_measure || ingredient.planned_unit;
      
      // Calculate variance by converting raw material amount to formulation units for comparison
      let varianceAmount = null;
      let variancePercentage = null;
      if (ingredient.planned_amount && rawMaterial?.primary_unit_measure) {
        try {
          // Convert actual raw material amount to formulation units for variance calculation
          const actualInFormulationUnits = convertUnits(
            totalQuantityUsedInRawUnits,
            rawMaterial.primary_unit_measure,  // from gal
            ingredient.planned_unit,           // to kg
            rawMaterial.density_g_ml || 1.0
          );
          
          const density = rawMaterial.density_g_ml;
          const varianceCalc = calculateVariance(
            ingredient.planned_amount,     // planned in kg
            ingredient.planned_unit,       // kg
            actualInFormulationUnits,      // actual converted to kg for comparison
            ingredient.planned_unit,       // kg
            density
          );
          
          if (varianceCalc) {
            varianceAmount = varianceCalc.variance;
            variancePercentage = varianceCalc.variancePercentage;
          }
        } catch (error) {
          console.warn('Failed to calculate variance with unit conversion:', error);
        }
      }

      // Get supplier lot/batch numbers from selections
      const supplierLotNumbers = selectedLots.map(lot => lot.lot_number).filter(Boolean);
      const supplierBatchNumbers = selectedLots.map(lot => lot.batch_number).filter(Boolean);
      
      // Update the main batch ingredient record - store in RAW MATERIAL UNITS
      const updateData = {
        actual_amount: actualAmount,        // Raw material amount (233.012 gal)
        actual_unit: actualUnit,            // Raw material unit (gal)
        variance_amount: varianceAmount,
        variance_percentage: variancePercentage,
        supplier_lot_number: supplierLotNumbers.length > 0 ? supplierLotNumbers.join(', ') : null,
        supplier_batch_number: supplierBatchNumbers.length > 0 ? supplierBatchNumbers.join(', ') : null
      };
      
      const { data: updatedData, error: updateError } = await supabase
        .from('batch_ingredients')
        .update(updateData)
        .eq('id', ingredient.id)
        .select();

      if (updateError) {
        throw new Error(`Failed to update batch ingredient: ${updateError.message}`);
      }

      // Delete existing lot selections for this ingredient
      const { data: deletedData, error: deleteError } = await supabase
        .from('batch_ingredient_lots')
        .delete()
        .eq('batch_ingredient_id', ingredient.id)
        .select();

      if (deleteError) {
        throw new Error(`Failed to clear existing lot selections: ${deleteError.message}`);
      }

      // Insert new lot selections to database
      if (selectedLots.length > 0) {
        // Validate all lot records have required data
        const validLots = selectedLots.filter(lot => 
          lot.inventory_item_id && 
          lot.quantity_used > 0 && 
          (lot.lot_number || lot.batch_number)
        );
        
        if (validLots.length === 0) {
          throw new Error('No valid lot selections to save');
        }
        
        const lotRecords = validLots.map(lot => ({
          batch_ingredient_id: ingredient.id,
          inventory_item_id: lot.inventory_item_id,
          quantity_used: Number(lot.quantity_used),
          unit_used: lot.unit || ingredient.planned_unit || ingredient.raw_materials?.primary_unit_measure || 'gal',
          lot_number: lot.lot_number || null,
          batch_number: lot.batch_number || null,
          expiry_date: lot.expiry_date || null,
          manufactured_date: lot.manufactured_date || null,
          location_name: lot.location_name || null,
          created_by: user.id
        }));

        const { data: insertedData, error: insertError } = await supabase
          .from('batch_ingredient_lots')
          .insert(lotRecords)
          .select();

        if (insertError) {
          throw new Error(`Failed to save lot selections: ${insertError.message}`);
        }
      }

      // Update local state
      const updatedSelections = { ...lotBatchSelections };
      updatedSelections[ingredient.id] = selectedLots;
      setLotBatchSelections(updatedSelections);

      // Close dialog and refresh data - force multiple refreshes to ensure UI updates
      setShowLotBatchDialog(false);
      await loadBatchIngredients(false); // Refresh without feedback
      await loadAllLotSummaries();
      refreshEditDialogIfOpen(ingredient.id);
      
      // Force a second refresh after a short delay to ensure UI updates
      setTimeout(async () => {
        await loadBatchIngredients(false);
        await loadAllLotSummaries();
      }, 500);
      
      const lotCount = selectedLots.length;
      // Calculate equivalent formulation amount for display message
      let equivalentFormulationAmount = totalQuantityUsedInRawUnits;
      if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== ingredient.planned_unit) {
        try {
          equivalentFormulationAmount = convertUnits(
            totalQuantityUsedInRawUnits,
            rawMaterial.primary_unit_measure,
            ingredient.planned_unit,
            rawMaterial.density_g_ml || 1.0
          );
        } catch (error) {
          equivalentFormulationAmount = totalQuantityUsedInRawUnits;
        }
      }
      
      showSnackbar(
        `Lot/batch selections saved! Used ${lotCount} lot${lotCount === 1 ? '' : 's'}: ${totalQuantityUsedInRawUnits.toFixed(3)} ${actualUnit} (≈${equivalentFormulationAmount.toFixed(3)} ${ingredient.planned_unit})`, 
        'success'
      );
      checkAndUpdateCompletion();
      
    } catch (error) {
      console.error('Error saving lot/batch selections:', error);
      showSnackbar('Error saving lot/batch selections: ' + error.message, 'error');
    } finally {
      setSavingLotSelections(false);
    }
  };

  const closeLotBatchDialog = () => {
    setShowLotBatchDialog(false);
    setSelectedIngredientForLotBatch(null);
    setAvailableLots([]);
    setSelectedLots([]);
    setRemainingQuantityNeeded(0);
    setLotSearch('');
  };

  const loadAllLotSummaries = async () => {
    try {
      const summaries = {};
      for (const ingredient of ingredients) {
        const selections = await loadExistingLotSelections(ingredient.id, ingredient.raw_material_id);
        if (selections && selections.length > 0) {
          const totalQuantity = selections.reduce((sum, sel) => sum + (sel.quantity_used || 0), 0);
          const lotCount = selections.length;
          
          summaries[ingredient.id] = {
            totalQuantity,
            lotCount,
            lots: selections
          };
        }
      }
      setLotSummaries(summaries);
    } catch (error) {
      console.error('Error loading lot summaries:', error);
    }
  };

  const getLotBatchSummary = (ingredientId) => {
    return lotSummaries[ingredientId] || null;
  };

  // Group ingredients by phase
  const groupedIngredients = ingredients.reduce((groups, ingredient) => {
    const phaseId = ingredient.formulation_phase_id || 'no-phase';
    const phaseName = ingredient.formulation_phases?.name || 'Unassigned Phase';
    const phaseSequence = ingredient.formulation_phases?.sequence || 999;
    
    if (!groups[phaseId]) {
      groups[phaseId] = {
        name: phaseName,
        sequence: phaseSequence,
        ingredients: []
      };
    }
    groups[phaseId].ingredients.push(ingredient);
    return groups;
  }, {});

  // Sort phases by sequence
  const sortedPhases = Object.entries(groupedIngredients).sort(
    ([, a], [, b]) => a.sequence - b.sequence
  );

  // Permission check for batch ingredients
  if (!canAccessPage('batch_ingredients')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Batch Ingredients Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            fontSize: '1.05rem',
            maxWidth: 450,
            mx: 'auto'
          }}>
            You don't currently have the necessary permissions to access the Batch Ingredients. 
            Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (!batch?.formulation_id) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <ScienceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No Formulation Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select a formulation in the Overview tab to view ingredients.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2,
      pl: 3,
      pr: 3,
      maxWidth: '98vw',
      width: '100%'
    }}>
      {/* Stock Availability Overview */}
      {ingredients.length > 0 && (
        <Accordion 
          expanded={accordionStates.inventoryOverview}
          onChange={(event, expanded) => updateAccordionState('inventoryOverview', expanded)}
          elevation={0}
          sx={{
            borderRadius: '16px !important',
            mb: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: 'hidden',
            '&:before': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
            sx={{
              background: 'white',
              backdropFilter: 'blur(10px)',
              '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)'
              }}>
                                <InventoryTwoIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
               <Box sx={{ flex: 1, ml: 2 }}>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                   Chemical Inventory Stock Status
                   {productionLocationName && (
                     <Chip 
                       label={productionLocationName}
                       size="small"
                       sx={{ 
                         ml: 2, 
                         background: `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                         color: 'white',
                         fontWeight: 500
                       }}
                     />
                   )}
                 </Typography>
                 <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                   {productionLocationName 
                     ? `Inventory levels at ${productionLocationName} for batch ingredients`
                     : 'Current inventory levels for batch ingredients'
                   }
                 </Typography>
               </Box>
              {loadingInventory && (
                <Box sx={{ ml: 'auto' }}>
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>

            {(() => {
              const formulationMaterialIds = new Set(
                formulationIngredients.map(fi => fi.ingredient_id).filter(Boolean)
              );
              const prodLocation = batch?.production_location;
              const isLocationId = prodLocation && prodLocation.includes('-') && prodLocation.length > 20;
              const chemicalTableData = inventoryData
                .filter(inv => {
                  if (!formulationMaterialIds.has(inv.raw_material_id)) return false;
                  if (!prodLocation) return true;
                  if (isLocationId) return inv.location?.id === prodLocation || inv.location_id === prodLocation;
                  return inv.location?.name === prodLocation;
                })
                .map(inv => ({
                  item_id: inv.raw_material_id,
                  item_name: inv.raw_material?.name || '',
                  item_sku: inv.raw_material?.sku || '',
                  location_id: inv.location_id,
                  location_name: inv.location?.name || '',
                  unit_type: inv.raw_material?.primary_unit_type || 'units',
                  unit_measure: inv.raw_material?.primary_unit_measure || '',
                  unit_qty_per_unit: inv.raw_material?.primary_unit_quantity || null,
                  total_quantity: inv.current_unit_quantity || 0,
                  total_base_quantity: inv.current_total_quantity || 0,
                  reorder_point: inv.reorder_point_units || 0,
                  reorder_quantity: inv.reorder_quantity || 0,
                  company_id: inv.company_id,
                  lots: inventoryItemsData
                    .filter(item => item.raw_material_id === inv.raw_material_id && item.location_id === inv.location_id)
                    .map(item => ({
                      ...item,
                      vendor_info: item.source_transaction ? {
                        supplier_name: item.source_transaction.supplier?.name || null,
                        unit_cost: item.source_transaction.unit_cost || null,
                        invoice_number: item.source_transaction.invoice_number || null,
                        invoice_date: item.source_transaction.invoice_date || null,
                        po_number: item.source_transaction.purchase_order_number || null,
                        po_date: null
                      } : null
                    }))
                }));
              return (
                <ChemicalTable
                  data={chemicalTableData}
                  config={addChemicalConfig}
                  inventoryType="chemicals"
                  expandable={true}
                  onRefresh={loadInventoryData}
                  noContainerBorder={true}
                  canEdit={false}
                  canDelete={false}
                />
              );
            })()}
            {false && <div>{[].map((formIngredient) => {
                    const rawMaterial = formIngredient.raw_materials;
                    if (!rawMaterial) return null;

                    const inventories = getInventoryForIngredient(rawMaterial.id);
                    
                    if (inventories.length === 0) {
                      return (
                        <TableRow key={`${rawMaterial.id}-no-inventory`} sx={{ bgcolor: '#ffebee' }}>
                          <TableCell></TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {rawMaterial.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {rawMaterial.sku && `SKU: ${rawMaterial.sku}`}
                                {rawMaterial.category && ` • ${rawMaterial.category}`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              No inventory tracked
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                              {rawMaterial.primary_unit_quantity || 0}{rawMaterial.primary_unit_measure} per {rawMaterial.primary_unit_type}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="error">
                              —
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              —
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label="No Inventory"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return inventories.map((item, index) => {
                      const stockStatus = getStockStatus(rawMaterial.id, formIngredient.planned_amount || 0);
                      const inventoryItems = getInventoryItemsForIngredient(rawMaterial.id, item.location_id);
                      const isExpanded = isInventoryRowExpanded(rawMaterial.id, item.location_id);
                      const hasExpiryInfo = inventoryItems.some(invItem => invItem.expiry_date);
                      const hasLotBatchInfo = inventoryItems.some(invItem => invItem.lot_number || invItem.batch_number);
                      
                      return (
                        <React.Fragment key={item.id}>
                          {/* Main inventory row */}
                          <TableRow 
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              bgcolor: (item.current_unit_quantity === 0 || item.current_total_quantity === 0) ? 
                                alpha(theme.palette.error.main, 0.02) : 
                                stockStatus.color === 'warning' ? alpha(theme.palette.warning.main, 0.02) : 
                                stockStatus.color === 'error' ? alpha(theme.palette.error.main, 0.02) : 'white',
                              '&:hover': { 
                                bgcolor: 'grey.50',
                                transform: 'translateX(4px)',
                                transition: 'all 0.2s ease',
                                boxShadow: 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 3,
                                px: 3
                              }
                            }}
                          >
                            <TableCell>
                              {inventoryItems.length > 0 ? (
                                <IconButton
                                  size="medium"
                                  onClick={() => toggleInventoryRowExpansion(rawMaterial.id, item.location_id)}
                                  sx={{ 
                                    color: 'primary.main',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                    borderRadius: 2,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': {
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                                </IconButton>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 400,
                                    color: 'text.primary',
                                    fontSize: '1.1rem',
                                    mb: 1
                                  }}
                                >
                                  {index === 0 && rawMaterial.name}
                                </Typography>
                                {index === 0 && (
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    {rawMaterial.sku && (
                                      <Chip 
                                        label={`SKU: ${rawMaterial.sku}`} 
                                        size="medium" 
                                        sx={{ 
                                          fontSize: '0.85rem',
                                          fontWeight: 400,
                                          height: 28
                                        }}
                                      />
                                    )}
                                    {rawMaterial.category && (
                                      <Chip 
                                        label={rawMaterial.category} 
                                        size="medium" 
                                        variant="outlined"
                                        sx={{ 
                                          fontSize: '0.85rem',
                                          fontWeight: 400,
                                          height: 28
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}
                                {inventoryItems.length > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Stack direction="row" spacing={0.5}>
                                      {hasLotBatchInfo && (
                                        <Chip 
                                          label={`${inventoryItems.length} lot${inventoryItems.length === 1 ? '' : 's'}`}
                                          size="medium"
                                          variant="outlined"
                                          sx={{ 
                                            fontSize: '0.85rem',
                                            fontWeight: 400,
                                            height: 28
                                          }}
                                        />
                                      )}
                                      {hasExpiryInfo && (
                                        <Chip 
                                          label="Expiry tracked"
                                          size="medium"
                                          variant="outlined"
                                          color="info"
                                          sx={{ 
                                            fontSize: '0.85rem',
                                            fontWeight: 400,
                                            height: 28
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 400,
                                  color: 'text.primary',
                                  fontSize: '1rem'
                                }}
                              >
                                {item.location?.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main', fontSize: '1.05rem', mb: 0.5 }}>
                                  {rawMaterial.primary_unit_quantity || 0}{rawMaterial.primary_unit_measure} per {rawMaterial.primary_unit_type}
                                </Typography>
                                {(() => {
                                  const material = rawMaterial;
                                  if (!material || !material.primary_unit_quantity) return null;
                                  
                                  const targetSize = parseFloat(material.primary_unit_quantity) || 1;
                                  const targetUnit = material.primary_unit_measure || 'g';
                                  const materialDensity = material.density_g_ml || 1.0;
                                  
                                  if (Math.abs(materialDensity - 1.0) <= 0.001) return null;
                                  
                                  try {
                                    const targetUnitType = getUnitType(targetUnit);
                                    
                                    if (targetUnitType === 'volume') {
                                      // Convert volume to weight using unitConversions.js
                                      const actualWeightKg = convertUnits(targetSize, targetUnit, 'kg', materialDensity);
                                      return (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          ≈ {actualWeightKg.toFixed(3)} kg per unit (density: {materialDensity.toFixed(3)} g/ml)
                                        </Typography>
                                      );
                                    } else if (targetUnitType === 'weight') {
                                      // Convert weight to volume using unitConversions.js
                                      const actualVolumeL = convertUnits(targetSize, targetUnit, 'L', materialDensity);
                                      return (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          ≈ {actualVolumeL.toFixed(3)} L per unit (density: {materialDensity.toFixed(3)} g/ml)
                                        </Typography>
                                      );
                                    }
                                  } catch (error) {
                                    console.warn('Unit conversion failed:', error);
                                    return null;
                                  }
                                })()}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="h5" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: (item.current_unit_quantity || 0) > 0 ? 'text.primary' : 'text.disabled',
                                  fontSize: '1.3rem',
                                  mb: 0.5
                                }}
                              >
                                {(item.current_unit_quantity || 0).toLocaleString()}
                              </Typography>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontWeight: 400,
                                  display: 'block',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {rawMaterial.primary_unit_type}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  fontWeight: 400,
                                  fontSize: '1.05rem',
                                  '&:hover': {
                                    color: 'primary.dark',
                                    fontWeight: 500
                                  }
                                }}
                              >
                                {item.reorder_point_units ? 
                                  `${item.reorder_point_units} ${rawMaterial.primary_unit_type}` : 
                                  'Not set'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={stockStatus.status === 'expired' ? 'Contains Expired' :
                                       stockStatus.status === 'expiring' ? 'Expiring Soon' :
                                       item.current_unit_quantity === 0 ? 'Out of Stock' : 
                                       (item.reorder_point_units > 0 && item.current_unit_quantity <= item.reorder_point_units) ? 'Low Stock' : 
                                       'In Stock'}
                                color={stockStatus.color}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded lot details */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                <Box sx={{ 
                                  borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                  bgcolor: alpha(theme.palette.grey[50], 0.8),
                                  p: 4,
                                  mb: 4,
                                  mt: 2,
                                  mx: 2,
                                  borderRadius: 3,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.06)}`
                                  }
                                }}>
                                  <Typography variant="h6" sx={{ 
                                    fontWeight: 500, 
                                    color: 'primary.main', 
                                    mb: 3,
                                    fontSize: '1.2rem'
                                  }}>
                                    Lot/Batch Details ({inventoryItems.length} lot{inventoryItems.length !== 1 ? 's' : ''})
                                  </Typography>
                                  
                                  <TableContainer component={Paper} sx={{ 
                                    borderRadius: 3, 
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                      overflow: 'hidden'
                                    }
                                  }}>
                                    <Table size="medium">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2, px: 3 }}>Quantity</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2, px: 3 }}>Lot/Batch Details</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2, px: 3 }}>Dates</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2, px: 3 }}>Supplier & Invoice</TableCell>
                                          <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1rem', py: 2, px: 3 }} align="center">Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {inventoryItems.map((invItem, lotIndex) => {
                                          const expiryStatus = formatExpirationStatus(invItem.expiry_date);
                                          const quantity = invItem.current_unit_quantity;
                                          const lotNumber = invItem.lot_number;
                                          const batchNumber = invItem.batch_number;
                                          const mfgDate = invItem.manufactured_date;
                                          const expDate = invItem.expiry_date;
                                          const sourceTransaction = invItem.source_transaction;
                                          const supplier = sourceTransaction?.supplier || invItem.supplier;
                                          
                                          const isExpired = expDate && new Date(expDate) < new Date();
                                          const isExpiringSoon = expDate && 
                                            new Date(expDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && // 30 days
                                            !isExpired;

                                          return (
                                            <TableRow 
                                              key={`lot-item-${invItem.id}-${lotIndex}`}
                                              sx={{ 
                                                transition: 'all 0.2s ease',
                                                '&:hover': { 
                                                  bgcolor: 'grey.50',
                                                  transform: 'translateX(4px)',
                                                  transition: 'all 0.2s ease',
                                                  boxShadow: 'inset 4px 0 0 black'
                                                },
                                                '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[50], 0.3) }
                                              }}
                                            >
                                              <TableCell sx={{ py: 2.5, px: 3 }}>
                                                <Typography 
                                                  variant="h5" 
                                                  sx={{ 
                                                    fontWeight: 500,
                                                    color: (quantity || 0) > 0 ? 'text.primary' : 'text.disabled',
                                                    fontSize: '1.3rem',
                                                    mb: 0.5
                                                  }}
                                                >
                                                  {(quantity || 0).toLocaleString()}
                                                </Typography>
                                                <Typography 
                                                  variant="body1" 
                                                  sx={{ 
                                                    color: 'text.secondary',
                                                    fontWeight: 400,
                                                    display: 'block',
                                                    fontSize: '0.9rem'
                                                  }}
                                                >
                                                  {rawMaterial.primary_unit_type}
                                                </Typography>
                                              </TableCell>
                                              <TableCell sx={{ py: 2.5, px: 3 }}>
                                                <Box>
                                                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                                    {lotNumber && (
                                                      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                                        Lot: {lotNumber}
                                                      </Typography>
                                                    )}
                                                    {batchNumber && (
                                                      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                                        {lotNumber ? ' • ' : ''}Batch: {batchNumber}
                                                      </Typography>
                                                    )}
                                                  </Typography>
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ py: 2.5, px: 3 }}>
                                                <Box>
                                                  {mfgDate && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                      Mfg: {new Date(mfgDate).toLocaleDateString()}
                                                    </Typography>
                                                  )}
                                                  {expDate && (
                                                    <Typography variant="caption" color={`${expiryStatus.color}.main`} sx={{ fontWeight: 500, display: 'block' }}>
                                                      Exp: {new Date(expDate).toLocaleDateString()}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </TableCell>
                                              <TableCell sx={{ py: 2.5, px: 3 }}>
                                                {(sourceTransaction?.supplier?.name || sourceTransaction?.supplier || supplier?.name || supplier) ? (
                                                  <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                      {sourceTransaction?.supplier?.name || sourceTransaction?.supplier || supplier?.name || supplier}
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <Typography variant="caption" color="text.secondary">
                                                    Supplier
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell align="center" sx={{ py: 2.5, px: 3 }}>
                                                <Chip 
                                                  label={isExpired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'In Stock'}
                                                  color={isExpired ? 'error' : isExpiringSoon ? 'warning' : 'success'}
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    });
                  })}</div>}
          </AccordionDetails>
        </Accordion>
      )}

      <Grid container spacing={3}>
        {/* Panel A: Ingredient Summary */}
        <Grid item xs={12} lg={3.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                {/* Enhanced Header */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                        Batch Ingredients
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Production tracking
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Scaling Information */}
                {scalingFactor !== 1 && (
                  <Accordion 
                    expanded={accordionStates.scalingInfo}
                    onChange={(event, expanded) => updateAccordionState('scalingInfo', expanded)}
                    elevation={0}
                    sx={{
                      borderRadius: '12px !important',
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '16px 0 16px 0',
                        borderRadius: '12px !important'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& .MuiAccordion-region': {
                        borderRadius: '0 0 12px 12px !important'
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.04)
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <ScaleIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          Scaling: {scalingFactor.toFixed(3)}x
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const formulation = formulations.find(f => f.id === batch.formulation_id);
                          const targetSize = batch.planned_quantity || 0;
                          const targetUnit = batch.planned_unit_of_measure || 'oz';
                          
                          let calculatedDensity = 1.0;
                          if (formulationIngredients.length > 0) {
                            let totalWeightedDensity = 0;
                            let totalPercentage = 0;
                            
                            formulationIngredients.forEach(ingredient => {
                              const rawMaterial = ingredient.raw_materials;
                              const percentage = ingredient.percentage || 0;
                              const materialDensity = rawMaterial?.density_g_ml;
                              
                              if (materialDensity && materialDensity > 0 && percentage > 0) {
                                totalWeightedDensity += (materialDensity * percentage);
                                totalPercentage += percentage;
                              }
                            });
                            
                            if (totalPercentage > 0) {
                              calculatedDensity = totalWeightedDensity / totalPercentage;
                            }
                          }
                          
                          let actualVolumeDisplay = `${targetSize} ${targetUnit}`;
                          try {
                            const targetUnitType = getUnitType(targetUnit);
                            
                            if (targetUnitType === 'weight') {
                              const targetInGrams = convertUnits(targetSize, targetUnit, 'g');
                              const targetInML = targetInGrams / calculatedDensity;
                              const targetInGallons = convertUnits(targetInML, 'ml', 'gal');
                              actualVolumeDisplay = `${targetInGallons.toFixed(2)} gal (${targetSize} ${targetUnit} at ${calculatedDensity.toFixed(3)} g/ml)`;
                            } else {
                              const targetInGallons = convertUnits(targetSize, targetUnit, 'gal');
                              if (targetUnit !== 'gal') {
                                actualVolumeDisplay = `${targetInGallons.toFixed(2)} gal (${targetSize} ${targetUnit})`;
                              }
                            }
                          } catch (error) {
                            // Fallback to original display
                          }
                          
                          return `Formulation: ${formulation?.target_batch_size || 'N/A'} ${formulation?.target_batch_unit || 'gal'} → Target: ${actualVolumeDisplay}`;
                        })()}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Quick Stats */}
                {ingredients.length > 0 && (
                  <Accordion 
                    expanded={accordionStates.progressOverview}
                    onChange={(event, expanded) => updateAccordionState('progressOverview', expanded)}
                    elevation={0}
                    sx={{
                      borderRadius: '12px !important',
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '16px 0 16px 0',
                        borderRadius: '12px !important'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& .MuiAccordion-region': {
                        borderRadius: '0 0 12px 12px !important'
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.04)
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <VarianceIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          Progress Overview
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {/* Overall Progress */}
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Completion Rate
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                            {Math.round((ingredients.filter(i => i.actual_amount).length / ingredients.length) * 100)}%
                          </Typography>
                        </Box>

                        <Divider />

                        {/* Phase Breakdown */}
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            By Phase
                          </Typography>
                          <Stack spacing={1}>
                            {sortedPhases.map(([phaseId, phase]) => {
                              const completed = phase.ingredients.filter(i => i.actual_amount).length;
                              const total = phase.ingredients.length;
                              const percentage = total > 0 ? (completed / total) * 100 : 0;
                              
                              return (
                                <Box key={phaseId} sx={{ 
                                  p: 2,
                                  borderRadius: 3,
                                  border: `1px solid ${theme.palette.grey[400]}`,
                                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                  backdropFilter: 'blur(10px)',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                  }
                                }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      Phase {phase.sequence}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: percentage === 100 ? '#2e7d32' : '#000000' }}>
                                      {completed}/{total}
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel B: Ingredients by Phase */}
        <Grid item xs={12} lg={5.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                Production Ingredients
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#000000' }} />
                </Box>
              ) : ingredients.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ScienceIcon sx={{ fontSize: 48, color: '#666666', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No Ingredients Generated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generate ingredients from the formulation to start tracking.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {sortedPhases.map(([phaseId, phase]) => (
                    <Accordion 
                      key={phaseId}
                      expanded={accordionStates.phases[phaseId] || false}
                      onChange={(event, expanded) => updatePhaseAccordionState(phaseId, expanded)}
                      elevation={0}
                      sx={{
                        borderRadius: '12px !important',
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(20px)',
                        overflow: 'hidden',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '&:before': {
                          display: 'none'
                        },
                        '&.Mui-expanded': {
                          margin: '8px 0 8px 0',
                          borderRadius: '12px !important'
                        },
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& .MuiAccordion-region': {
                          borderRadius: '0 0 12px 12px !important'
                        }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                        sx={{
                          background: 'white',
                          backdropFilter: 'blur(10px)',
                          minHeight: 56,
                          '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.04)
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                            Phase {phase.sequence}: {phase.name}
                          </Typography>
                          <Chip 
                            label={`${phase.ingredients.length} ingredient${phase.ingredients.length === 1 ? '' : 's'}`}
                            size="medium"
                            sx={{ 
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              height: 28,
                              bgcolor: theme.palette.grey[300],
                              color: theme.palette.primary.main
                            }}
                          />
                          <Box sx={{ flex: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {phase.ingredients.filter(i => i.actual_amount).length}/{phase.ingredients.length}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                          {phase.ingredients.map((ingredient) => (
                            <Box
                              key={ingredient.id}
                              sx={{ 
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `white`,
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                }
                              }}
                            >
                              {/* Ingredient Header */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                                  {ingredient.raw_materials?.name || 'Unknown'}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatPercentage(ingredient.planned_percentage)}
                                  </Typography>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      • {formatAmount(ingredient.planned_amount, ingredient.planned_unit)}
                                    </Typography>
                                    {(() => {
                                      const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                                        ingredient.planned_amount,
                                        ingredient.planned_unit,
                                        ingredient.raw_materials
                                      );
                                      return rawMaterialConversion ? (
                                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500, fontSize: '0.7rem' }}>
                                          ≈{rawMaterialConversion.amount} {rawMaterialConversion.unit} raw
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </Box>
                                </Stack>
                                {ingredient.raw_materials && (
                                  <Box>
                                    {(() => {
                                      const stockStatus = getStockStatus(ingredient.raw_materials.id, ingredient.planned_amount || 0);
                                      const totalStock = getTotalStockForIngredient(ingredient.raw_materials.id);
                                      const inventoryItems = getInventoryItemsForIngredient(ingredient.raw_materials.id);
                                      const hasLotBatchInfo = inventoryItems.some(item => item.lot_number || item.batch_number);
                                      const expiringItems = inventoryItems.filter(item => {
                                        if (!item.expiry_date) return false;
                                        const daysToExpiry = (new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24);
                                        return daysToExpiry <= 30 && daysToExpiry >= 0;
                                      });
                                      const expiredItems = inventoryItems.filter(item => {
                                        if (!item.expiry_date) return false;
                                        return new Date(item.expiry_date) < new Date();
                                      });
                                      
                                      return (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                          <Chip 
                                            label={`Stock: ${totalStock} ${ingredient.raw_materials.primary_unit_type}`}
                                            color={stockStatus.color}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem' }}
                                          />
                                          {hasLotBatchInfo && (
                                            <Chip 
                                              label={`${inventoryItems.length} lot${inventoryItems.length === 1 ? '' : 's'}`}
                                              size="small"
                                              variant="outlined"
                                              color="info"
                                              sx={{ fontSize: '0.7rem' }}
                                            />
                                          )}
                                          {expiredItems.length > 0 && (
                                            <Chip 
                                              label={`${expiredItems.length} expired`}
                                              size="small"
                                              variant="outlined"
                                              color="error"
                                              sx={{ fontSize: '0.7rem' }}
                                            />
                                          )}
                                          {expiringItems.length > 0 && expiredItems.length === 0 && (
                                            <Chip 
                                              label={`${expiringItems.length} expiring`}
                                              size="small"
                                              variant="outlined"
                                              color="warning"
                                              sx={{ fontSize: '0.7rem' }}
                                            />
                                          )}
                                        </Stack>
                                      );
                                    })()}
                                  </Box>
                                )}
                              </Box>

                              {/* Actual Amount */}
{canEditPage('batch_ingredients') ? (
                                <Box
                                  onClick={() => handleLotBatchAmountClick(ingredient)}
                                  sx={{ 
                                    p: 2,
                                    borderRadius: 3,
                                    border: `1px solid ${ingredient.actual_amount ? theme.palette.success.main : theme.palette.warning.main}`,
                                    cursor: 'pointer',
                                    background: ingredient.actual_amount ? 
                                      `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})` : 
                                      `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { 
                                      transform: 'translateY(-2px)',
                                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    mb: 1.5
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    {(() => {
                                      const lotSummary = getLotBatchSummary(ingredient.id);
                                      
                                      if (lotSummary) {
                                        return (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                Actual: {(() => {
                                                  // Show actual amount in raw material units with formulation equivalent
                                                  const rawMaterial = ingredient.raw_materials;
                                                  if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== ingredient.planned_unit) {
                                                    // Find the actual amount stored (should be in raw material units now)
                                                    const actualAmount = ingredient.actual_amount || lotSummary.totalQuantity;
                                                    const actualUnit = ingredient.actual_unit || ingredient.planned_unit;
                                                    
                                                    // If stored in raw material units, show raw + conversion
                                                    if (actualUnit === rawMaterial.primary_unit_measure) {
                                                      try {
                                                        const equivalentFormulation = convertUnits(
                                                          actualAmount,
                                                          actualUnit,
                                                          ingredient.planned_unit,
                                                          rawMaterial.density_g_ml || 1.0
                                                        );
                                                        return `${actualAmount.toFixed(3)} ${actualUnit} (≈${equivalentFormulation.toFixed(3)} ${ingredient.planned_unit})`;
                                                      } catch (error) {
                                                        return `${actualAmount.toFixed(3)} ${actualUnit}`;
                                                      }
                                                    }
                                                  }
                                                  // Fallback to original display
                                                  return formatAmount(lotSummary.totalQuantity, ingredient.planned_unit);
                                                })()}
                                              </Typography>
                                              <Chip 
                                                label={`${lotSummary.lotCount} lot${lotSummary.lotCount === 1 ? '' : 's'}`}
                                                size="small"
                                                sx={{ 
                                                  height: '20px', 
                                                  fontSize: '0.7rem',
                                                  bgcolor: theme.palette.grey[300],
                                                  color: theme.palette.primary.main
                                                }}
                                              />
                                            </Stack>
                                          </Box>
                                        );
                                      } else {
                                        return (
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                            Click to select lot/batch
                                          </Typography>
                                        );
                                      }
                                    })()}
                                    
                                    {(() => {
                                      // Calculate live variance instead of using database value
                                      let liveVariancePercentage = ingredient.variance_percentage;
                                      
                                      if (ingredient.actual_amount && ingredient.planned_amount) {
                                        try {
                                          const rawMaterial = ingredient.raw_materials;
                                          const density = rawMaterial?.density_g_ml || 1.0;
                                          
                                          // Convert actual amount to planned units for comparison
                                          let actualInPlannedUnits = ingredient.actual_amount;
                                          if (ingredient.actual_unit !== ingredient.planned_unit) {
                                            actualInPlannedUnits = convertUnits(
                                              ingredient.actual_amount,
                                              ingredient.actual_unit,
                                              ingredient.planned_unit,
                                              density
                                            );
                                          }
                                          
                                          const variance = actualInPlannedUnits - ingredient.planned_amount;
                                          liveVariancePercentage = (variance / ingredient.planned_amount) * 100;
                                        } catch (error) {
                                          console.warn('Live variance calculation failed:', error);
                                        }
                                      }
                                      
                                      return liveVariancePercentage && Math.abs(liveVariancePercentage) > 0.01 ? (
                                        <Chip 
                                          label={`${liveVariancePercentage > 0 ? '+' : ''}${liveVariancePercentage.toFixed(1)}%`}
                                          color={getVarianceColor(liveVariancePercentage)}
                                          size="small"
                                        />
                                      ) : null;
                                    })()}
                                  </Stack>
                                </Box>
                              ) : (
                                <Box
                                  sx={{ 
                                    p: 2,
                                    borderRadius: 3,
                                    border: `1px solid ${ingredient.actual_amount ? theme.palette.success.main : theme.palette.grey[400]}`,
                                    background: ingredient.actual_amount ? 
                                      `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})` : 
                                      `linear-gradient(${alpha(theme.palette.grey[300], 0.1)}, ${alpha(theme.palette.grey[300], 0.05)})`,
                                    backdropFilter: 'blur(10px)',
                                    mb: 1.5
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    {(() => {
                                      const lotSummary = getLotBatchSummary(ingredient.id);
                                      
                                      if (lotSummary) {
                                        return (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                Actual: {(() => {
                                                  // Show actual amount in raw material units with formulation equivalent
                                                  const rawMaterial = ingredient.raw_materials;
                                                  if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== ingredient.planned_unit) {
                                                    // Find the actual amount stored (should be in raw material units now)
                                                    const actualAmount = ingredient.actual_amount || lotSummary.totalQuantity;
                                                    const actualUnit = ingredient.actual_unit || ingredient.planned_unit;
                                                    
                                                    // If stored in raw material units, show raw + conversion
                                                    if (actualUnit === rawMaterial.primary_unit_measure) {
                                                      try {
                                                        const equivalentFormulation = convertUnits(
                                                          actualAmount,
                                                          actualUnit,
                                                          ingredient.planned_unit,
                                                          rawMaterial.density_g_ml || 1.0
                                                        );
                                                        return `${actualAmount.toFixed(3)} ${actualUnit} (≈${equivalentFormulation.toFixed(3)} ${ingredient.planned_unit})`;
                                                      } catch (error) {
                                                        return `${actualAmount.toFixed(3)} ${actualUnit}`;
                                                      }
                                                    }
                                                  }
                                                  // Fallback to original display
                                                  return formatAmount(lotSummary.totalQuantity, ingredient.planned_unit);
                                                })()}
                                              </Typography>
                                              <Chip 
                                                label={`${lotSummary.lotCount} lot${lotSummary.lotCount === 1 ? '' : 's'}`}
                                                size="small"
                                                sx={{ 
                                                  height: '20px', 
                                                  fontSize: '0.7rem',
                                                  bgcolor: theme.palette.grey[300],
                                                  color: theme.palette.primary.main
                                                }}
                                              />
                                            </Stack>
                                          </Box>
                                        );
                                      } else {
                                        return (
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                            No lot/batch selected
                                          </Typography>
                                        );
                                      }
                                    })()}
                                    
                                    {(() => {
                                      // Calculate live variance instead of using database value
                                      let liveVariancePercentage = ingredient.variance_percentage;
                                      
                                      if (ingredient.actual_amount && ingredient.planned_amount) {
                                        try {
                                          const rawMaterial = ingredient.raw_materials;
                                          const density = rawMaterial?.density_g_ml || 1.0;
                                          
                                          // Convert actual amount to planned units for comparison
                                          let actualInPlannedUnits = ingredient.actual_amount;
                                          if (ingredient.actual_unit !== ingredient.planned_unit) {
                                            actualInPlannedUnits = convertUnits(
                                              ingredient.actual_amount,
                                              ingredient.actual_unit,
                                              ingredient.planned_unit,
                                              density
                                            );
                                          }
                                          
                                          const variance = actualInPlannedUnits - ingredient.planned_amount;
                                          liveVariancePercentage = (variance / ingredient.planned_amount) * 100;
                                        } catch (error) {
                                          console.warn('Live variance calculation failed:', error);
                                        }
                                      }
                                      
                                      return liveVariancePercentage && Math.abs(liveVariancePercentage) > 0.01 ? (
                                        <Chip 
                                          label={`${liveVariancePercentage > 0 ? '+' : ''}${liveVariancePercentage.toFixed(1)}%`}
                                          color={getVarianceColor(liveVariancePercentage)}
                                          size="small"
                                        />
                                      ) : null;
                                    })()}
                                  </Stack>
                                </Box>
                              )}

                              {/* Quick Actions */}
                              <Stack spacing={1.5}>
                                {/* Show lot/batch info if selected */}
                                {(() => {
                                  const lotSummary = getLotBatchSummary(ingredient.id);
                                  return lotSummary && lotSummary.lots.length > 0 ? (
                                    <Box sx={{ 
                                      p: 2,
                                      borderRadius: 3,
                                      border: `1px solid ${theme.palette.success.main}`,
                                      background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                                      backdropFilter: 'blur(10px)',
                                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                      }
                                    }}>
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#2e7d32', display: 'block', mb: 1 }}>
                                        Selected Lots/Batches:
                                      </Typography>
                                      <Stack spacing={0.5}>
                                        {lotSummary.lots.map((lot, index) => (
                                          <Typography key={index} variant="caption" color="text.secondary">
                                            • {lot.lot_number || lot.batch_number} ({lot.quantity_used} {lot.unit})
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </Box>
                                  ) : null;
                                })()}
                                
                                {/* Action Row - Notes & Quality */}
                                {canEditPage('batch_ingredients') ? (
                                  <Stack direction="row" spacing={1}>
                                    <Tooltip title={ingredient.usage_notes || 'Add usage notes'}>
                                      <Button
                                        size="medium"
                                        onClick={(e) => handleNotesClick(e, ingredient)}
                                        sx={{
                                          flex: 1,
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                          background: theme.palette.grey[300],
                                          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                          borderRadius: 2,
                                          backdropFilter: 'blur(10px)',
                                          fontSize: '0.8rem',
                                          py: 1,
                                          px: 1.5,
                                          minHeight: '40px',
                                          textTransform: 'none',
                                          '&:hover': {
                                            background: theme.palette.grey[400]
                                          }
                                        }}
                                      >
                                        {ingredient.usage_notes 
                                          ? ingredient.usage_notes.length > 18 
                                            ? `${ingredient.usage_notes.substring(0, 18)}...`
                                            : ingredient.usage_notes
                                          : 'Notes'
                                        }
                                      </Button>
                                    </Tooltip>
                                    <Button
                                      size="medium"
                                      onClick={(e) => handleConditionClick(e, ingredient)}
                                      variant={ingredient.material_condition ? "contained" : "outlined"}
                                      sx={{
                                        flex: 1,
                                        bgcolor: ingredient.material_condition ? getConditionColor(ingredient.material_condition) === 'success' ? '#4caf50' : 
                                               getConditionColor(ingredient.material_condition) === 'warning' ? '#ff9800' : '#f44336' : 'transparent', borderRadius: 2,
                                        color: ingredient.material_condition ? '#ffffff' : '#000000',
                                        borderColor: '#000000',
                                        fontSize: '0.8rem',
                                        py: 1,
                                        px: 1.5,
                                        minHeight: '40px',
                                        textTransform: 'none',
                                        '&:hover': {
                                          bgcolor: ingredient.material_condition ? 
                                            (getConditionColor(ingredient.material_condition) === 'success' ? '#388e3c' : 
                                             getConditionColor(ingredient.material_condition) === 'warning' ? '#f57c00' : '#d32f2f') : '#f5f5f5'
                                        }
                                      }}
                                    >
                                      {ingredient.material_condition ? `Quality: ${ingredient.material_condition}` : 'Set Quality'}
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={1}>
                                    <Box
                                      sx={{
                                        flex: 1,
                                        p: 1,
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: theme.palette.grey[200],
                                        textAlign: 'center',
                                        minHeight: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        {ingredient.usage_notes 
                                          ? ingredient.usage_notes.length > 18 
                                            ? `${ingredient.usage_notes.substring(0, 18)}...`
                                            : ingredient.usage_notes
                                          : 'No notes'
                                        }
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        flex: 1,
                                        p: 1,
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: ingredient.material_condition ? 
                                          (getConditionColor(ingredient.material_condition) === 'success' ? alpha('#4caf50', 0.2) : 
                                           getConditionColor(ingredient.material_condition) === 'warning' ? alpha('#ff9800', 0.2) : alpha('#f44336', 0.2)) : 
                                          theme.palette.grey[200],
                                        textAlign: 'center',
                                        minHeight: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        {ingredient.material_condition ? `Quality: ${ingredient.material_condition}` : 'No quality set'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                )}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel C: Analytics & Progress */}
        <Grid item xs={12} lg={3}>
          <Stack spacing={3}>
            {/* Quick Actions Hub */}
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Quick Actions Hub
                </Typography>
                
                <Stack spacing={2}>
                  {/* Mass Actions */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Batch Operations
                    </Typography>
                    <Stack spacing={1}>
{canEditPage('batch_ingredients') && (
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          disabled={loading}
                          onClick={async () => {
                            if (ingredients.length === 0) {
                              showSnackbar('No ingredients to update', 'info');
                              return;
                            }
                            
                            setLoading(true);
                            try {
                              // Update ALL ingredients to "Good" condition - override existing values
                              const toUpdate = ingredients;
                              
                              // Update in batches to avoid overwhelming the database
                              for (let i = 0; i < toUpdate.length; i += 10) {
                                const batch = toUpdate.slice(i, i + 10);
                                const updates = batch.map(ingredient => 
                                  supabase
                                    .from('batch_ingredients')
                                    .update({ material_condition: 'Good' })
                                    .eq('id', ingredient.id)
                                );
                                await Promise.all(updates);
                              }
                              
                              await loadBatchIngredients();
                              showSnackbar(`${toUpdate.length} materials set to Good condition`, 'success');
                              checkAndUpdateCompletion();
                            } catch (error) {
                              console.error('Error updating conditions:', error);
                              showSnackbar('Error updating conditions: ' + error.message, 'error');
                            } finally {
                              setLoading(false);
                            }
                          }}
                          sx={{
                            borderColor: '#4caf50',
                            color: '#4caf50',
                            borderRadius: 2,
                            '&:hover': { borderColor: '#388e3c', bgcolor: '#f1f8e9' }
                          }}
                        >
                          {loading ? 'Updating...' : 'Mark All Materials as Good'}
                        </Button>
                      )}

                    </Stack>
                  </Box>

                  <Divider />

                  {/* Production Notes */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Production Notes
                    </Typography>
                    <Stack spacing={1}>
{canEditPage('batch_ingredients') && (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          size="small"
                          label="Batch Notes"
                          placeholder="Enter production notes, observations, issues, or special handling instructions..."
                          value={productionNotes}
                          onChange={(e) => setProductionNotes(e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      )}
{canEditPage('batch_ingredients') && (
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={savingProductionNotes ? <CircularProgress size={16} /> : <SaveIcon />}
                          disabled={savingProductionNotes || !productionNotes.trim()}
                          onClick={async () => {
                            if (!productionNotes.trim()) return;
                            
                            setSavingProductionNotes(true);
                            try {
                              const { error } = await supabase
                                .from('batch_notes')
                                .insert({
                                  batch_id: batch.id,
                                  note_content: productionNotes,
                                  note_type: 'general',
                                  created_by: user.id
                                });
                              
                              if (error) throw error;
                              
                              await loadBatchNotes();
                              showSnackbar('Production notes saved successfully!', 'success');
                              setProductionNotes('');
                            } catch (error) {
                              console.error('Error saving notes:', error);
                              showSnackbar('Error saving notes: ' + error.message, 'error');
                            } finally {
                              setSavingProductionNotes(false);
                            }
                          }}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            background: 'rgba(255, 255, 255, 0.95)',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }
                          }}
                        >
                          {savingProductionNotes ? 'Saving...' : 'Save Notes'}
                        </Button>
                      )}
                      
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={loadingBatchNotes ? <CircularProgress size={16} /> : <NotesIcon />}
                        disabled={loadingBatchNotes}
                        onClick={() => {
                          if (batchNotes.length > 0) {
                            setShowSavedNotesDialog(true);
                          } else {
                            showSnackbar('No saved notes for this batch yet', 'info');
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          color: theme.palette.text.secondary,
                          fontWeight: 500,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                          }
                        }}
                      >
                        {loadingBatchNotes ? 'Loading...' : `View Notes (${batchNotes.length})`}
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* System Actions */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      System Actions
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                          loadBatchIngredients(true);
                          loadInventoryData();
                        }}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          background: theme.palette.grey[400],
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: theme.palette.grey[400]
                          }
                        }}
                      >
                        Refresh All Data
                      </Button>
                      
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveProgress}
                        disabled={!canEditPage('batch_ingredients')}
                        sx={{
                          borderColor: canEditPage('batch_ingredients') ? '#000000' : theme.palette.grey[400],
                          borderRadius: 2,
                          color: canEditPage('batch_ingredients') ? '#000000' : theme.palette.grey[500],
                          '&:hover': { 
                            borderColor: canEditPage('batch_ingredients') ? '#000000' : theme.palette.grey[400], 
                            bgcolor: canEditPage('batch_ingredients') ? '#f5f5f5' : 'transparent' 
                          },
                          '&:disabled': {
                            borderColor: theme.palette.grey[400],
                            color: theme.palette.grey[500]
                          }
                        }}
                      >
                        Save Progress
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Variance Summary */}
            {ingredients.some(i => i.variance_percentage) && (
              <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                    Variance Analysis
                  </Typography>
                  
                  <Stack spacing={2}>
                    {(() => {
                      // Check for ingredients with significant variance
                      const ingredientsWithVariance = ingredients
                        .filter(i => i.actual_amount && i.planned_amount)
                        .map((ingredient) => {
                          // Calculate live variance for each ingredient
                          let liveVariancePercentage = 0;
                          
                          try {
                            const rawMaterial = ingredient.raw_materials;
                            const density = rawMaterial?.density_g_ml || 1.0;
                            
                            // Convert actual amount to planned units for comparison
                            let actualInPlannedUnits = ingredient.actual_amount;
                            if (ingredient.actual_unit !== ingredient.planned_unit) {
                              actualInPlannedUnits = convertUnits(
                                ingredient.actual_amount,
                                ingredient.actual_unit,
                                ingredient.planned_unit,
                                density
                              );
                            }
                            
                            const variance = actualInPlannedUnits - ingredient.planned_amount;
                            liveVariancePercentage = (variance / ingredient.planned_amount) * 100;
                          } catch (error) {
                            console.warn('Live variance calculation failed for analysis card:', error);
                            liveVariancePercentage = ingredient.variance_percentage || 0;
                          }
                          
                          return {
                            ingredient,
                            variance: liveVariancePercentage
                          };
                        })
                        .filter(item => Math.abs(item.variance) > 0.01);

                      // If no significant variances, show success message
                      if (ingredientsWithVariance.length === 0) {
                        const totalIngredientsWithActual = ingredients.filter(i => i.actual_amount && i.planned_amount).length;
                        
                        if (totalIngredientsWithActual > 0) {
                          return (
                            <Box sx={{ 
                              p: 3,
                              borderRadius: 3,
                              border: `1px solid ${theme.palette.success.main}`,
                              background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                              backdropFilter: 'blur(10px)',
                              textAlign: 'center'
                            }}>
                              <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', mb: 0.5 }}>
                                Excellent Accuracy!
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                All ingredients are using planned amounts with proper unit conversions
                              </Typography>
                            </Box>
                          );
                        }
                        
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No variance data available yet
                          </Typography>
                        );
                      }

                      // Show ingredients with significant variance
                      return ingredientsWithVariance.slice(0, 3).map((item) => (
                        <Box key={item.ingredient.id} sx={{ 
                          p: 2,
                          borderRadius: 3,
                          border: `1px solid ${Math.abs(item.variance) > 5 ? theme.palette.error.main : theme.palette.success.main}`,
                          background: Math.abs(item.variance) > 5 ? 
                            `linear-gradient(${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})` : 
                            `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                          backdropFilter: 'blur(10px)'
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                            {item.ingredient.raw_materials?.name}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              Variance
                            </Typography>
                            <Chip 
                              label={`${item.variance > 0 ? '+' : ''}${item.variance.toFixed(1)}%`}
                              color={getVarianceColor(item.variance)}
                              size="small"
                            />
                          </Stack>
                        </Box>
                      ));
                    })()}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Edit Ingredient Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <EditIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
                Edit Ingredient Usage
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Update actual quantities and supplier information
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          backdropFilter: 'blur(10px)'
        }}>
          {editingIngredient && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Ingredient Header */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: 3,
                  border: '1px solid #2196f3'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                    {editingIngredient.raw_materials?.name}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Planned: {formatAmount(editingIngredient.planned_amount, editingIngredient.planned_unit)}
                    </Typography>
                    {(() => {
                      const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                        editingIngredient.planned_amount,
                        editingIngredient.planned_unit,
                        editingIngredient.raw_materials
                      );
                      return rawMaterialConversion ? (
                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}>
                          ≈{rawMaterialConversion.amount} {rawMaterialConversion.unit} raw (density: {rawMaterialConversion.density} g/ml)
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                  {editingIngredient.raw_materials?.primary_unit_measure && (
                    <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
                      Material Primary Unit: {editingIngredient.raw_materials.primary_unit_measure}
                      {editingIngredient.raw_materials.primary_unit_quantity && 
                        ` (${editingIngredient.raw_materials.primary_unit_quantity}${editingIngredient.raw_materials.primary_unit_measure} per ${editingIngredient.raw_materials.primary_unit_type})`
                      }
                    </Typography>
                  )}
                </Card>
              </Grid>

              {unitConversionWarning && (
                <Grid item xs={12}>
                  <Alert 
                    severity={unitConversionWarning.includes('Warning') ? 'warning' : 'info'} 
                    sx={{ 
                      borderRadius: 2,
                      border: '1px solid #ff9800',
                      fontWeight: 500
                    }}
                  >
                    {unitConversionWarning}
                  </Alert>
                </Grid>
              )}
              
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Actual Amount"
                  type="number"
                  value={editingIngredient.actual_amount}
                  onChange={(e) => setEditingIngredient({
                    ...editingIngredient,
                    actual_amount: e.target.value
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {editingIngredient.actual_unit}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#000000', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: '#000000', borderWidth: 2 },
                      '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: 2 }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={4}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#000000', borderWidth: 2 },
                    '&:hover fieldset': { borderColor: '#000000', borderWidth: 2 },
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: 2 }
                  }
                }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={editingIngredient.actual_unit}
                    label="Unit"
                    onChange={(e) => handleUnitChange(e.target.value)}
                  >
                    {getAllUnits().map(unit => (
                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ fontWeight: 500 }}>
                    Planned unit: {editingIngredient.planned_unit}
                    {editingIngredient.actual_unit !== editingIngredient.planned_unit && 
                      ` • Will convert for variance calculation`
                    }
                  </FormHelperText>
                </FormControl>
              </Grid>
              
              {/* Lot/Batch Selection Status */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 2, 
                  backgroundColor: '#fff3e0', 
                  borderRadius: 2,
                  border: '1px solid #ff9800'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                    Lot/Batch Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use the "Click to select lot/batch" button in the ingredients list to choose specific lots/batches for this ingredient.
                    This will automatically set supplier lot/batch numbers and ensure proper inventory tracking.
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#000000', borderWidth: 2 },
                    '&:hover fieldset': { borderColor: '#000000', borderWidth: 2 },
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: 2 }
                  }
                }}>
                  <InputLabel>Material Condition</InputLabel>
                  <Select
                    value={editingIngredient.material_condition}
                    label="Material Condition"
                    onChange={(e) => setEditingIngredient({
                      ...editingIngredient,
                      material_condition: e.target.value
                    })}
                  >
                    <MenuItem value="Good">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <span>Good</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="Acceptable">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningIcon color="warning" fontSize="small" />
                        <span>Acceptable</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="Damaged">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningIcon color="error" fontSize="small" />
                        <span>Damaged</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="Expired">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <WarningIcon color="error" fontSize="small" />
                        <span>Expired</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Usage Notes"
                  multiline
                  rows={3}
                  value={editingIngredient.usage_notes}
                  onChange={(e) => setEditingIngredient({
                    ...editingIngredient,
                    usage_notes: e.target.value
                  })}
                  placeholder="Enter any notes about ingredient usage, quality, or special handling..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#000000', borderWidth: 2 },
                      '&:hover fieldset': { borderColor: '#000000', borderWidth: 2 },
                      '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: 2 }
                    }
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setUnitConversionWarning('');
            }} 
            startIcon={<CancelIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveIngredient}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Condition Change Menu */}
      <Menu
        anchorEl={conditionMenuAnchor}
        open={Boolean(conditionMenuAnchor)}
        onClose={handleConditionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={() => handleConditionChange('Good')}>
          <Chip label="Good" color="success" size="small" sx={{ mr: 1 }} />
          Good
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Acceptable')}>
          <Chip label="Acceptable" color="warning" size="small" sx={{ mr: 1 }} />
          Acceptable
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Damaged')}>
          <Chip label="Damaged" color="error" size="small" sx={{ mr: 1 }} />
          Damaged
        </MenuItem>
        <MenuItem onClick={() => handleConditionChange('Expired')}>
          <Chip label="Expired" color="error" size="small" sx={{ mr: 1 }} />
          Expired
        </MenuItem>
      </Menu>

      {/* Quick Amount Edit Menu */}
      <Menu
        anchorEl={amountEditAnchor}
        open={Boolean(amountEditAnchor)}
        onClose={handleAmountEditClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { 
            p: 2, 
            minWidth: 280,
            maxWidth: 320
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
            Set Actual Amount
          </Typography>
          {selectedIngredientForAmount && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {selectedIngredientForAmount.raw_materials?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Planned: {formatAmount(selectedIngredientForAmount.planned_amount, selectedIngredientForAmount.planned_unit)}
              </Typography>
              {(() => {
                const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                  selectedIngredientForAmount.planned_amount,
                  selectedIngredientForAmount.planned_unit,
                  selectedIngredientForAmount.raw_materials
                );
                return rawMaterialConversion ? (
                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500, mt: 0.5, fontSize: '0.7rem' }}>
                    ≈{rawMaterialConversion.amount} {rawMaterialConversion.unit} raw
                  </Typography>
                ) : null;
              })()}
            </Box>
          )}
        </Box>
        
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Actual Amount"
            type="number"
            value={quickEditAmount}
            onChange={(e) => setQuickEditAmount(e.target.value)}
            size="small"
            inputProps={{ step: 0.001 }}
          />
          
          <FormControl fullWidth size="small">
            <InputLabel>Unit</InputLabel>
            <Select
              value={quickEditUnit}
              label="Unit"
              onChange={(e) => setQuickEditUnit(e.target.value)}
            >
              {getAllUnits().map(unit => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleUsePlannedAmount}
            fullWidth
            sx={{ 
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Use Planned Amount ({selectedIngredientForAmount?.planned_amount} {selectedIngredientForAmount?.planned_unit})
          </Button>
          
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleAmountEditClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleQuickAmountSave}
              disabled={!quickEditAmount || savingAmount}
              fullWidth
              startIcon={savingAmount ? <CircularProgress size={16} /> : null}
            >
              {savingAmount ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Menu>



      {/* Quick Notes Edit Menu */}
      <Menu
        anchorEl={notesEditAnchor}
        open={Boolean(notesEditAnchor)}
        onClose={handleNotesEditClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { 
            p: 2, 
            minWidth: 320,
            maxWidth: 400
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
            Set Usage Notes
          </Typography>
          {selectedIngredientForNotes && (
            <Typography variant="caption" color="text.secondary">
              {selectedIngredientForNotes.raw_materials?.name}
            </Typography>
          )}
        </Box>
        
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Usage Notes"
            multiline
            rows={3}
            value={quickEditNotes}
            onChange={(e) => setQuickEditNotes(e.target.value)}
            size="small"
            placeholder="Enter any notes about ingredient usage, quality, or special handling..."
          />
          
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleNotesEditClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleQuickNotesSave}
              disabled={savingNotes}
              fullWidth
              startIcon={savingNotes ? <CircularProgress size={16} /> : null}
            >
              {savingNotes ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Menu>

      {/* Generate Confirmation Dialog */}
      <Dialog
        open={showGenerateConfirm}
        onClose={() => setShowGenerateConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: '#ff9800',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha('#ff9800', 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 4
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon sx={{ color: '#ff9800' }} />
            <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 500}}>
              Confirm Generate from Formulation
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 4,
          backdropFilter: 'blur(10px)',
          mt: 3
        }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              backgroundColor: alpha('#ff9800', 0.1),
              border: `1px solid ${alpha('#ff9800', 0.3)}`,
              '& .MuiAlert-icon': {
                color: '#ff9800'
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary}}>
              This will reset all existing ingredient data
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              The following data will be permanently lost:
            </Typography>
            <Box component="ul" sx={{ 
              mt: 1, 
              mb: 0, 
              pl: 2,
              '& li': {
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
                lineHeight: 1.6
              }
            }}>
              <li>Actual amounts entered</li>
              <li>Supplier lot and batch numbers</li>
              <li>Usage notes</li>
              <li>Quality conditions</li>
            </Box>
          </Alert>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 500,
              textAlign: 'center',
              mt: 2
            }}
          >
            Are you sure you want to continue and regenerate ingredients from the formulation?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.3),
          gap: 2
        }}>
          <Button 
            onClick={() => {
              setShowGenerateConfirm(false);
              showSnackbar('Generation cancelled - existing data preserved', 'info');
            }}
            startIcon={<CancelIcon />}
            size="large"
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              color: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.grey[300], 0.8),
              border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[400], 0.8),
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowGenerateConfirm(false);
              generateBatchIngredients();
            }}
            variant="contained"
            startIcon={<ScienceIcon />}
            size="large"
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              bgcolor: '#ff9800',
              color: 'white',
              border: `1px solid ${alpha('#ff9800', 0.8)}`,
              boxShadow: `0 4px 12px ${alpha('#ff9800', 0.3)}`,
              '&:hover': {
                bgcolor: '#f57c00',
                boxShadow: `0 6px 16px ${alpha('#ff9800', 0.4)}`,
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Generate & Reset Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Saved Notes Dialog */}
      <Dialog
        open={showSavedNotesDialog}
        onClose={() => setShowSavedNotesDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 4
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <NotesIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 500 }}>
              Saved Production Notes ({batchNotes.length})
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ mt: 3 }}>
            {batchNotes.length === 0 ? (
              <Alert severity="info">No production notes available for this batch.</Alert>
            ) : (
              <Stack spacing={2}>
                {batchNotes.map((note) => (
                  <Card key={note.id} variant="outlined" sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    }
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      {editingNote?.id === note.id ? (
                        <Stack spacing={2}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Editing note from {new Date(note.created_at).toLocaleString()}
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editNoteContent}
                            onChange={(e) => setEditNoteContent(e.target.value)}
                            size="small"
                            placeholder="Enter production notes..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { 
                                  borderColor: alpha(theme.palette.grey[400], 0.8), 
                                  borderWidth: 1,
                                  borderRadius: 2
                                },
                                '&:hover fieldset': { 
                                  borderColor: alpha(theme.palette.primary.main, 0.4), 
                                  borderWidth: 1 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: theme.palette.primary.main, 
                                  borderWidth: 1 
                                }
                              }
                            }}
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              onClick={cancelEditNote}
                              disabled={savingEditNote}
                              sx={{
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={saveEditNote}
                              disabled={savingEditNote || !editNoteContent.trim()}
                              startIcon={savingEditNote ? <CircularProgress size={16} /> : null}
                              sx={{
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: 'white',
                                fontWeight: 500,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                '&:disabled': {
                                  background: theme.palette.grey[400],
                                  color: theme.palette.grey[100]
                                }
                              }}
                            >
                              {savingEditNote ? 'Saving...' : 'Save'}
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Box flex={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {new Date(note.created_at).toLocaleString()} • {note.note_type}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {note.note_content}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
{canEditPage('batch_ingredients') && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => startEditNote(note)}
                                sx={{ mt: -0.5 }}
                              >
                                <EditNoteIcon fontSize="small" />
                              </IconButton>
                            )}
{canDeletePage('batch_ingredients') && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteBatchNote(note.id)}
                                disabled={deletingNoteId === note.id}
                                sx={{ mt: -0.5 }}
                              >
                                {deletingNoteId === note.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setShowSavedNotesDialog(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Close
          </Button>
{batchNotes.length > 0 && canDeletePage('batch_ingredients') && (
            <Button
              onClick={deleteAllBatchNotes}
              variant="contained"
              startIcon={loadingBatchNotes ? <CircularProgress size={16} /> : <DeleteIcon />}
              disabled={loadingBatchNotes}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: '#d32f2f',
                color: 'white',
                fontWeight: 500,
                '&:hover': {
                  background: '#b71c1c'
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {loadingBatchNotes ? 'Deleting...' : 'Delete All Notes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Lot/Batch Selection Dialog */}
      <Dialog
        open={showLotBatchDialog}
        onClose={closeLotBatchDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          mb: 6
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <LotIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
                Select Lot/Batch for {selectedIngredientForLotBatch?.raw_materials?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Choose specific lots/batches to use for this ingredient
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: '80px 24px 24px 24px',
          backdropFilter: 'blur(10px)'
        }}>
          <Stack spacing={4}>
            {/* Summary Info */}
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.04), 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Planned Amount:</Typography>
                  <Typography variant="h6">
                    {selectedIngredientForLotBatch?.planned_amount} {selectedIngredientForLotBatch?.planned_unit}
                  </Typography>
                  {(() => {
                    const rawMaterial = selectedIngredientForLotBatch?.raw_materials;
                    const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                      selectedIngredientForLotBatch?.planned_amount,
                      selectedIngredientForLotBatch?.planned_unit,
                      rawMaterial
                    );
                    return rawMaterialConversion ? (
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}>
                        ≈{rawMaterialConversion.amount} {rawMaterialConversion.unit} raw needed
                      </Typography>
                    ) : null;
                  })()}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Remaining Needed:</Typography>
                  <Typography 
                    variant="h6" 
                    color={remainingQuantityNeeded > 0 ? 'warning.main' : 'success.main'}
                  >
                    {remainingQuantityNeeded.toFixed(3)} {(() => {
                      const rawMaterial = selectedIngredientForLotBatch?.raw_materials;
                      return rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== selectedIngredientForLotBatch?.planned_unit
                        ? rawMaterial.primary_unit_measure
                        : selectedIngredientForLotBatch?.planned_unit;
                    })()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Selected Lots */}
            {selectedLots.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Selected Lots/Batches ({selectedLots.length})
                </Typography>
                <Stack spacing={1}>
                  {selectedLots.map((lot, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography variant="body2" color="textSecondary">
                            {lot.lot_number ? 'Lot:' : 'Batch:'}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {lot.lot_number || lot.batch_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="textSecondary">Available:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                            {lot.available_units} {lot.unit_type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            = {(() => {
                              // Find the original lot from availableLots to get the raw inventory quantity
                              const originalLot = availableLots.find(al => al.id === lot.inventory_item_id);
                              return originalLot?.current_total_quantity || lot.available_total_quantity || 0;
                            })()} {lot.unit_measure} available
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="textSecondary">Using ({lot.unit_measure}):</Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={lot.quantity_used || ''}
                            onChange={(e) => updateLotQuantity(index, e.target.value)}
                            inputProps={{ 
                              min: 0.1, 
                              max: (() => {
                                // Get raw inventory quantity - no weird calculations
                                const originalLot = availableLots.find(al => al.id === lot.inventory_item_id);
                                return originalLot?.current_total_quantity || lot.available_total_quantity || 0;
                              })(),
                              step: 0.1 
                            }}
                            error={(() => {
                              const maxQty = (() => {
                                const originalLot = availableLots.find(al => al.id === lot.inventory_item_id);
                                return originalLot?.current_total_quantity || lot.available_total_quantity || 0;
                              })();
                              return (lot.quantity_used > maxQty) || (lot.quantity_used <= 0 && lot.quantity_used !== '');
                            })()}
                            helperText={(() => {
                              const maxQty = (() => {
                                const originalLot = availableLots.find(al => al.id === lot.inventory_item_id);
                                return originalLot?.current_total_quantity || lot.available_total_quantity || 0;
                              })();
                              return lot.quantity_used > maxQty ? 
                                `Max: ${maxQty} ${lot.unit_measure}` : 
                                lot.quantity_used <= 0 && lot.quantity_used !== '' ?
                                'Must be greater than 0' :
                                `Max: ${maxQty} ${lot.unit_measure || 'gal'}`;
                            })()}
                            sx={{ 
                              mt: 0.5,
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-error fieldset': { 
                                  borderColor: '#f44336',
                                  borderWidth: 2
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#000000'
                                }
                              },
                              '& .MuiFormHelperText-root': {
                                fontSize: '0.7rem'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="textSecondary">Location:</Typography>
                          <Typography variant="body2">
                            {lot.location_name || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            onClick={() => removeLotFromSelection(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                      
                      {lot.expiry_date && (
                        <Box sx={{ mt: 1 }}>
                          {(() => {
                            const expiryStatus = formatExpirationStatus(lot.expiry_date);
                            return (
                              <Chip
                                label={expiryStatus.text}
                                color={expiryStatus.color}
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            );
                          })()}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Available Lots */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="h6">
                  Available Lots/Batches ({availableLots.length})
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    // Auto-fill lots with planned amount in RAW MATERIAL UNITS
                    const plannedAmount = selectedIngredientForLotBatch?.planned_amount || 0;
                    const plannedUnit = selectedIngredientForLotBatch?.planned_unit;
                    const rawMaterial = selectedIngredientForLotBatch?.raw_materials;
                    
                    // Convert planned amount to raw material units if different
                    let plannedAmountInRawUnits = plannedAmount;
                    
                    if (rawMaterial?.primary_unit_measure && rawMaterial.primary_unit_measure !== plannedUnit) {
                      try {
                        plannedAmountInRawUnits = convertUnits(
                          plannedAmount,
                          plannedUnit,
                          rawMaterial.primary_unit_measure,
                          rawMaterial.density_g_ml || 1.0
                        );
                      } catch (error) {
                        console.warn('Failed to convert planned amount to raw material units for auto-fill:', error);
                      }
                    }
                    
                    let remainingToFill = plannedAmountInRawUnits;
                    const autoSelections = [];
                    
                    // Use lots in their current order without any sorting
                    for (const lot of availableLots) {
                      if (remainingToFill <= 0) break;
                      
                      const availableQuantity = lot.current_total_quantity || 0;
                      const quantityToUse = Math.min(remainingToFill, availableQuantity);
                      
                      if (quantityToUse > 0) {
                        autoSelections.push({
                          inventory_item_id: lot.id,
                          lot_number: lot.lot_number,
                          batch_number: lot.batch_number,
                          available_units: lot.current_unit_quantity || 0,
                          available_total_quantity: availableQuantity,
                          quantity_used: quantityToUse,
                          unit: selectedIngredientForLotBatch.planned_unit,
                          unit_type: lot.raw_material?.primary_unit_type || selectedIngredientForLotBatch?.raw_materials?.primary_unit_type || 'units',
                          unit_measure: lot.raw_material?.primary_unit_measure || selectedIngredientForLotBatch?.raw_materials?.primary_unit_measure || selectedIngredientForLotBatch?.planned_unit || 'gal',
                          expiry_date: lot.expiry_date,
                          location_name: lot.location?.name,
                          manufactured_date: lot.manufactured_date
                        });
                        
                        remainingToFill -= quantityToUse;
                      }
                    }
                    
                    setSelectedLots(autoSelections);
                    setRemainingQuantityNeeded(Math.max(0, remainingToFill));
                    
                    const actualUsed = plannedAmountInRawUnits - remainingToFill;
                    const displayUnit = rawMaterial?.primary_unit_measure || plannedUnit;
                    showSnackbar(`Auto-filled ${autoSelections.length} lots for ${actualUsed.toFixed(3)} ${displayUnit}`, 'success');
                  }}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                    }
                  }}
                >
                  Auto-Fill Planned Amount
                </Button>
              </Stack>

              <TextField
                size="small"
                placeholder="Search lot / batch number..."
                value={lotSearch}
                onChange={(e) => setLotSearch(e.target.value)}
                sx={{ mb: 2, width: '100%' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
              />

              {(() => {
                const q = lotSearch.trim().toLowerCase();
                const filteredLots = q
                  ? availableLots.filter(lot =>
                      (lot.lot_number || '').toLowerCase().includes(q) ||
                      (lot.batch_number || '').toLowerCase().includes(q)
                    )
                  : availableLots;
                if (filteredLots.length === 0) return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InventoryTwoIcon fontSize="large" color="disabled" />
                    <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                      {q ? `No lots match "${lotSearch}"` : 'No available lots/batches found for this ingredient'}
                    </Typography>
                  </Box>
                );
                return (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          align="left"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Lot/Batch
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Available Qty
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Location
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Manufactured
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Expiry Status
                        </TableCell>
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Certificates
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLots.map((lot) => {
                        const isSelected = selectedLots.some(sel => sel.inventory_item_id === lot.id);
                        const expiryStatus = formatExpirationStatus(lot.expiry_date);
                        
                        return (
                          <TableRow 
                            key={lot.id}
                            hover
                            onClick={() => selectLotForUse(lot)}
                            sx={{
                              cursor: isSelected ? 'not-allowed' : 'pointer',
                              opacity: isSelected ? 0.7 : 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: isSelected ? 'inherit' : 'grey.50',
                                transform: isSelected ? 'none' : 'translateX(4px)',
                                transition: 'all 0.2s ease',
                                boxShadow: isSelected ? 'none' : 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 2.5,
                                px: 3
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    color: theme.palette.text.primary,
                                    mb: 0.5
                                  }}
                                >
                                  {lot.lot_number || lot.batch_number || 'N/A'}
                                </Typography>
                                {isSelected && (
                                  <Chip 
                                    label="Selected" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'green' }}>
                                {lot.current_total_quantity || 0} {lot.raw_material?.primary_unit_measure || 'gal'} available
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Original: {lot.original_total_quantity || lot.current_total_quantity} {lot.raw_material?.primary_unit_measure || 'gal'}
                              </Typography>
                              {lot.available_quantity_after_allocations && lot.available_quantity_after_allocations < (lot.original_total_quantity || lot.current_total_quantity) && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                  {(lot.original_total_quantity || lot.current_total_quantity) - lot.available_quantity_after_allocations} {lot.raw_material?.primary_unit_measure || 'gal'} allocated
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {lot.location?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {lot.manufactured_date ? 
                                  new Date(lot.manufactured_date).toLocaleDateString() : 
                                  'N/A'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {expiryStatus.text && (
                                <Chip
                                  label={expiryStatus.text}
                                  color={expiryStatus.color}
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5}>
                                {lot.coa_file && (
                                  <Tooltip title="COA Available">
                                    <CheckCircleIcon fontSize="small" color="success" />
                                  </Tooltip>
                                )}
                                {lot.msds_file && (
                                  <Tooltip title="MSDS Available">
                                    <DescriptionIcon fontSize="small" color="primary" />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                );
              })()}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={closeLotBatchDialog}
            startIcon={<CloseIcon />}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveLotBatchSelections}
            variant="contained"
            startIcon={savingLotSelections ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={
              selectedLots.length === 0 || 
              savingLotSelections ||
              selectedLots.some(lot => 
                (lot.quantity_used > (lot.available_total_quantity || 0)) || 
                (lot.quantity_used <= 0 || !lot.quantity_used)
              )
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {savingLotSelections 
              ? 'Saving...' 
              : `Save Selections (${selectedLots.length} lot${selectedLots.length === 1 ? '' : 's'})`
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchIngredients; 